// Full lesson content for all 10 levels — unique per lesson, no reuse

export type Language = 'fr' | 'ru';

export interface QuizOption {
  id: string;
  text: { fr: string; ru: string };
  isCorrect: boolean;
}

export interface QuizQuestion {
  id: string;
  question: { fr: string; ru: string };
  options: QuizOption[];
}

export interface TheoryStep {
  type: 'theory';
  title: { fr: string; ru: string };
  content: { fr: string; ru: string };
  keyConceptFr: string;
  keyConceptRu: string;
}

export interface ExampleStep {
  type: 'example';
  title: { fr: string; ru: string };
  content: { fr: string; ru: string };
  noteToRememberFr: string;
  noteToRememberRu: string;
}

export interface ExerciseItem {
  id: string;
  account: { fr: string; ru: string };
  correctAnswer: 'debit' | 'credit';
}

export interface ExerciseStep {
  type: 'exercise';
  title: { fr: string; ru: string };
  question: { fr: string; ru: string };
  items: ExerciseItem[];
}

export interface QuizStep {
  type: 'quiz';
  title: { fr: string; ru: string };
  questions: QuizQuestion[];
}

export type LessonStep = TheoryStep | ExampleStep | ExerciseStep | QuizStep;

export interface LessonData {
  id: string;
  title: { fr: string; ru: string };
  steps: LessonStep[];
}

// ─── LEVEL TEST (unique per level) ───────────────────────────────────────────
export interface LevelTest {
  levelNumber: number;
  title: { fr: string; ru: string };
  passingScore: number; // percentage
  questions: QuizQuestion[];
}

// ─── LEVEL 1 LESSONS ─────────────────────────────────────────────────────────

const l_01_01: LessonData = {
  id: 'l-01-01',
  title: { fr: "Qu'est-ce que la comptabilité?", ru: 'Что такое бухгалтерия?' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Définition de la comptabilité', ru: 'Определение бухгалтерии' },
      content: {
        fr: `La **comptabilité** est le système d'enregistrement, de classification et d'analyse des transactions financières d'une entreprise.

Elle permet de:
- Suivre les entrées et sorties d'argent
- Mesurer la performance de l'entreprise
- Respecter les obligations légales et fiscales
- Prendre des décisions éclairées

La comptabilité est souvent appelée **"le langage des affaires"** car elle traduit toutes les activités économiques en chiffres compréhensibles.`,
        ru: `**Бухгалтерия** — это система записи, классификации и анализа финансовых операций предприятия.

Она позволяет:
- Отслеживать поступления и расходы денег
- Измерять эффективность предприятия
- Соблюдать правовые и налоговые обязательства
- Принимать обоснованные решения

Бухгалтерию часто называют **"языком бизнеса"**, поскольку она переводит все экономические операции в понятные цифры.`,
      },
      keyConceptFr: 'La comptabilité enregistre toutes les transactions financières pour donner une image fidèle de la situation de l\'entreprise.',
      keyConceptRu: 'Бухгалтерия записывает все финансовые операции, чтобы дать точное представление о состоянии предприятия.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple concret', ru: 'Конкретный пример' },
      content: {
        fr: `**Exemple:** Une boulangerie vend des croissants pour 200 € le lundi.

La comptabilité enregistre:
- **Recette:** +200 € (argent reçu)
- **Vente:** +200 € (produit généré)

À la fin du mois, le comptable peut dire:
- Chiffre d'affaires total: 6 000 €
- Charges (farine, loyer, salaires): 4 500 €
- **Résultat net: +1 500 €** (bénéfice)`,
        ru: `**Пример:** Пекарня продаёт круассаны на 200 € в понедельник.

Бухгалтерия записывает:
- **Выручка:** +200 € (полученные деньги)
- **Продажа:** +200 € (полученный доход)

В конце месяца бухгалтер может сказать:
- Общий оборот: 6 000 €
- Расходы (мука, аренда, зарплаты): 4 500 €
- **Чистая прибыль: +1 500 €** (прибыль)`,
      },
      noteToRememberFr: 'Chaque transaction commerciale doit être enregistrée avec la date, le montant et la description.',
      noteToRememberRu: 'Каждая коммерческая операция должна быть записана с датой, суммой и описанием.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Identifier les transactions', ru: 'Упражнение: Определите операции' },
      question: {
        fr: 'Une entreprise reçoit 1 000 € d\'un client. Classifiez les comptes affectés:',
        ru: 'Предприятие получает 1 000 € от клиента. Классифицируйте затронутые счета:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Caisse (argent reçu)', ru: 'Касса (полученные деньги)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Ventes (produit)', ru: 'Продажи (доход)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Introduction à la comptabilité', ru: 'Тест — Введение в бухгалтерию' },
      questions: [
        {
          id: 'l01-01-q1',
          question: { fr: 'Quel est le rôle principal de la comptabilité?', ru: 'Какова основная роль бухгалтерии?' },
          options: [
            { id: 'a', text: { fr: 'Décorer les bureaux', ru: 'Украшать офисы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Enregistrer et analyser les transactions financières', ru: 'Записывать и анализировать финансовые операции' }, isCorrect: true },
            { id: 'c', text: { fr: 'Gérer les ressources humaines', ru: 'Управлять человеческими ресурсами' }, isCorrect: false },
            { id: 'd', text: { fr: 'Créer des publicités', ru: 'Создавать рекламу' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-01-q2',
          question: { fr: 'La comptabilité est souvent appelée:', ru: 'Бухгалтерию часто называют:' },
          options: [
            { id: 'a', text: { fr: 'Le langage des affaires', ru: 'Языком бизнеса' }, isCorrect: true },
            { id: 'b', text: { fr: 'Le langage des mathématiques', ru: 'Языком математики' }, isCorrect: false },
            { id: 'c', text: { fr: 'Le langage des sciences', ru: 'Языком науки' }, isCorrect: false },
            { id: 'd', text: { fr: 'Le langage des arts', ru: 'Языком искусства' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-01-q3',
          question: { fr: 'Laquelle de ces activités est une fonction comptable?', ru: 'Какая из этих деятельностей является бухгалтерской функцией?' },
          options: [
            { id: 'a', text: { fr: 'Recruter des employés', ru: 'Нанимать сотрудников' }, isCorrect: false },
            { id: 'b', text: { fr: 'Enregistrer les ventes et les achats', ru: 'Записывать продажи и покупки' }, isCorrect: true },
            { id: 'c', text: { fr: 'Concevoir des produits', ru: 'Разрабатывать продукты' }, isCorrect: false },
            { id: 'd', text: { fr: 'Livrer des marchandises', ru: 'Доставлять товары' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_01_02: LessonData = {
  id: 'l-01-02',
  title: { fr: 'Les acteurs de la comptabilité', ru: 'Участники бухгалтерии' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Qui fait quoi en comptabilité?', ru: 'Кто что делает в бухгалтерии?' },
      content: {
        fr: `Plusieurs acteurs interviennent dans la comptabilité d'une entreprise:

**Le comptable** — Enregistre les transactions quotidiennes, prépare les états financiers.

**L'expert-comptable** — Certifie les comptes, conseille l'entreprise sur la fiscalité.

**Le commissaire aux comptes** — Vérifie et certifie les comptes des grandes entreprises.

**Le directeur financier (DAF)** — Supervise toute la fonction financière de l'entreprise. **L'administration fiscale** — Contrôle que l'entreprise respecte ses obligations fiscales.`,
        ru: `В бухгалтерии предприятия участвуют несколько субъектов:

**Бухгалтер** — Записывает ежедневные операции, готовит финансовую отчётность.

**Аудитор** — Удостоверяет счета, консультирует предприятие по налогообложению.

**Ревизор** — Проверяет и удостоверяет счета крупных предприятий.

**Финансовый директор (CFO)** — Контролирует всю финансовую функцию предприятия.

**Налоговая администрация** — Проверяет соблюдение предприятием налоговых обязательств.`,
      },
      keyConceptFr: 'Le comptable est le gardien des chiffres de l\'entreprise. Il assure la fiabilité des informations financières.',
      keyConceptRu: 'Бухгалтер — хранитель цифр предприятия. Он обеспечивает достоверность финансовой информации.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Rôles dans une PME', ru: 'Пример: Роли в малом бизнесе' },
      content: {
        fr: `**Scénario:** Une PME de 20 employés.

| Acteur | Rôle | Fréquence |
|--------|------|-----------|
| Comptable interne | Saisie des factures | Quotidien |
| Expert-comptable | Bilan annuel | Annuel |
| Administration fiscale | Déclaration TVA | Mensuel |

**Résultat:** Chaque acteur joue un rôle complémentaire pour garantir la conformité et la performance financière.`,
        ru: `**Сценарий:** Малое предприятие с 20 сотрудниками.

| Субъект | Роль | Частота |
|---------|------|---------|
| Внутренний бухгалтер | Ввод счетов | Ежедневно |
| Аудитор | Годовой баланс | Ежегодно |
| Налоговая администрация | Декларация НДС | Ежемесячно |

**Результат:** Каждый субъект играет дополнительную роль для обеспечения соответствия и финансовой эффективности.`,
      },
      noteToRememberFr: 'Dans une petite entreprise, le dirigeant peut lui-même tenir la comptabilité, mais il est conseillé de faire appel à un expert-comptable.',
      noteToRememberRu: 'В малом предприятии руководитель может сам вести бухгалтерию, но рекомендуется обратиться к аудитору.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Associer les rôles', ru: 'Упражнение: Сопоставьте роли' },
      question: {
        fr: 'Un client paie une facture de 500 €. Qui enregistre cette transaction?',
        ru: 'Клиент оплачивает счёт на 500 €. Кто записывает эту операцию?',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Caisse (argent entrant)', ru: 'Касса (входящие деньги)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Créances clients (dette payée)', ru: 'Дебиторская задолженность (оплаченный долг)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Les acteurs', ru: 'Тест — Участники' },
      questions: [
        {
          id: 'l01-02-q1',
          question: { fr: 'Qui certifie les comptes des grandes entreprises?', ru: 'Кто удостоверяет счета крупных предприятий?' },
          options: [
            { id: 'a', text: { fr: 'Le comptable interne', ru: 'Внутренний бухгалтер' }, isCorrect: false },
            { id: 'b', text: { fr: 'Le commissaire aux comptes', ru: 'Ревизор' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le directeur commercial', ru: 'Коммерческий директор' }, isCorrect: false },
            { id: 'd', text: { fr: 'Le responsable RH', ru: 'Менеджер по персоналу' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-02-q2',
          question: { fr: 'Quelle est la principale mission du comptable?', ru: 'Какова основная миссия бухгалтера?' },
          options: [
            { id: 'a', text: { fr: 'Vendre des produits', ru: 'Продавать продукты' }, isCorrect: false },
            { id: 'b', text: { fr: 'Enregistrer les transactions et préparer les états financiers', ru: 'Записывать операции и готовить финансовую отчётность' }, isCorrect: true },
            { id: 'c', text: { fr: 'Gérer les stocks', ru: 'Управлять запасами' }, isCorrect: false },
            { id: 'd', text: { fr: 'Recruter du personnel', ru: 'Нанимать персонал' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-02-q3',
          question: { fr: 'L\'administration fiscale contrôle:', ru: 'Налоговая администрация контролирует:' },
          options: [
            { id: 'a', text: { fr: 'La qualité des produits', ru: 'Качество продуктов' }, isCorrect: false },
            { id: 'b', text: { fr: 'Le respect des obligations fiscales', ru: 'Соблюдение налоговых обязательств' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les conditions de travail', ru: 'Условия труда' }, isCorrect: false },
            { id: 'd', text: { fr: 'La satisfaction des clients', ru: 'Удовлетворённость клиентов' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_01_03: LessonData = {
  id: 'l-01-03',
  title: { fr: 'Les documents comptables', ru: 'Бухгалтерские документы' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Les documents essentiels', ru: 'Основные документы' },
      content: {
        fr: `Les documents comptables sont les **preuves écrites** de toutes les transactions.

**La facture** — Document émis lors d'une vente ou d'un achat. Elle prouve la transaction. **Le reçu / ticket de caisse** — Preuve d'un paiement en espèces.

**Le relevé bancaire** — Liste de toutes les opérations du compte bancaire.

**Le journal comptable** — Registre chronologique de toutes les écritures.

**Le grand livre** — Récapitulatif par compte de toutes les transactions.

**Le bilan** — Photographie de la situation financière à une date donnée.`,
        ru: `Бухгалтерские документы — это **письменные доказательства** всех операций.

**Счёт-фактура** — Документ, выставляемый при продаже или покупке. Подтверждает операцию.

**Квитанция / кассовый чек** — Доказательство оплаты наличными.

**Банковская выписка** — Список всех операций по банковскому счёту.

**Бухгалтерский журнал** — Хронологический реестр всех записей.

**Главная книга** — Сводка по счетам всех операций.

**Баланс** — Снимок финансового положения на определённую дату.`,
      },
      keyConceptFr: 'Pas de comptabilité sans document justificatif! Chaque écriture doit être appuyée par une pièce comptable.',
      keyConceptRu: 'Нет бухгалтерии без оправдательного документа! Каждая запись должна подкрепляться бухгалтерским документом.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Cycle d\'une facture', ru: 'Пример: Цикл счёта-фактуры' },
      content: {
        fr: `**Scénario:** Achat de fournitures de bureau pour 300 €.

**Étape 1:** Le fournisseur émet une **facture** de 300 €.
**Étape 2:** Le comptable enregistre la facture dans le **journal**.
**Étape 3:** Le paiement est effectué — le **relevé bancaire** le confirme.
**Étape 4:** L'écriture est reportée dans le **grand livre**.

| Document | Rôle |
|----------|------|
| Facture | Preuve de l'achat |
| Journal | Enregistrement chronologique |
| Relevé bancaire | Confirmation du paiement |
| Grand livre | Classement par compte |`,
        ru: `**Сценарий:** Покупка канцелярских товаров на 300 €.

**Шаг 1:** Поставщик выставляет **счёт-фактуру** на 300 €.
**Шаг 2:** Бухгалтер записывает счёт в **журнал**.
**Шаг 3:** Производится оплата — **банковская выписка** подтверждает это.
**Шаг 4:** Запись переносится в **главную книгу**.

| Документ | Роль |
|----------|------|
| Счёт-фактура | Доказательство покупки |
| Журнал | Хронологическая запись |
| Банковская выписка | Подтверждение оплаты |
| Главная книга | Классификация по счетам |`,
      },
      noteToRememberFr: 'Les documents comptables doivent être conservés pendant au moins 10 ans selon la loi française.',
      noteToRememberRu: 'Бухгалтерские документы должны храниться не менее 10 лет согласно французскому законодательству.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Classer les documents', ru: 'Упражнение: Классифицируйте документы' },
      question: {
        fr: 'L\'entreprise paie son loyer de 800 €. Classifiez les comptes:',
        ru: 'Предприятие платит аренду 800 €. Классифицируйте счета:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Charges de loyer (dépense)', ru: 'Расходы на аренду (расход)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (argent sorti)', ru: 'Банк (выбывшие деньги)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Documents comptables', ru: 'Тест — Бухгалтерские документы' },
      questions: [
        {
          id: 'l01-03-q1',
          question: { fr: 'Quel document prouve qu\'une vente a eu lieu?', ru: 'Какой документ подтверждает, что продажа состоялась?' },
          options: [
            { id: 'a', text: { fr: 'Le relevé bancaire', ru: 'Банковская выписка' }, isCorrect: false },
            { id: 'b', text: { fr: 'La facture', ru: 'Счёт-фактура' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le bilan', ru: 'Баланс' }, isCorrect: false },
            { id: 'd', text: { fr: 'Le grand livre', ru: 'Главная книга' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-03-q2',
          question: { fr: 'Le journal comptable est:', ru: 'Бухгалтерский журнал — это:' },
          options: [
            { id: 'a', text: { fr: 'Un récapitulatif annuel', ru: 'Годовая сводка' }, isCorrect: false },
            { id: 'b', text: { fr: 'Un registre chronologique des écritures', ru: 'Хронологический реестр записей' }, isCorrect: true },
            { id: 'c', text: { fr: 'Une liste des employés', ru: 'Список сотрудников' }, isCorrect: false },
            { id: 'd', text: { fr: 'Un document fiscal', ru: 'Налоговый документ' }, isCorrect: false },
          ],
        },
        {
          id: 'l01-03-q3',
          question: { fr: 'Combien de temps faut-il conserver les documents comptables en France?', ru: 'Сколько лет нужно хранить бухгалтерские документы во Франции?' },
          options: [
            { id: 'a', text: { fr: '3 ans', ru: '3 года' }, isCorrect: false },
            { id: 'b', text: { fr: '5 ans', ru: '5 лет' }, isCorrect: false },
            { id: 'c', text: { fr: '10 ans', ru: '10 лет' }, isCorrect: true },
            { id: 'd', text: { fr: '1 an', ru: '1 год' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 2 LESSONS ─────────────────────────────────────────────────────────

const l_02_01: LessonData = {
  id: 'l-02-01',
  title: { fr: 'Le principe de continuité', ru: 'Принцип непрерывности' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Principe de continuité d\'exploitation', ru: 'Принцип непрерывности деятельности' },
      content: {
        fr: `Le **principe de continuité** stipule que l'entreprise est présumée continuer son activité dans un avenir prévisible.

Ce principe implique que:
- Les actifs sont évalués à leur **valeur d'usage** (pas de liquidation)
- Les dettes sont enregistrées à leur **valeur nominale**
- Les amortissements sont calculés sur la **durée de vie** des biens

**Exception:** Si l'entreprise est en difficulté et risque de fermer, les comptes doivent être établis en **valeur de liquidation**.

Ce principe est l'un des **principes comptables fondamentaux** reconnus internationalement.`,
        ru: `**Принцип непрерывности** гласит, что предприятие предположительно продолжит свою деятельность в обозримом будущем.

Этот принцип означает, что:
- Активы оцениваются по **стоимости использования** (без ликвидации)
- Долги записываются по **номинальной стоимости**
- Амортизация рассчитывается на **срок службы** имущества

**Исключение:** Если предприятие испытывает трудности и рискует закрыться, счета должны составляться по **ликвидационной стоимости**.

Этот принцип является одним из **основных бухгалтерских принципов**, признанных на международном уровне.`,
      },
      keyConceptFr: 'Sans le principe de continuité, chaque bilan devrait être établi comme si l\'entreprise allait fermer demain.',
      keyConceptRu: 'Без принципа непрерывности каждый баланс должен был бы составляться так, как если бы предприятие закрывалось завтра.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Impact sur les actifs', ru: 'Пример: Влияние на активы' },
      content: {
        fr: `**Scénario:** Une machine achetée 10 000 € avec une durée de vie de 5 ans.

**Avec continuité d'exploitation:**
- Valeur comptable année 1: 10 000 - 2 000 = **8 000 €**
- Amortissement annuel: 2 000 € (10 000 / 5)

**Sans continuité (liquidation):**
- Valeur de revente immédiate: **3 000 €** (prix du marché d'occasion)

| Situation | Valeur comptable |
|-----------|-----------------|
| Continuité | 8 000 € |
| Liquidation | 3 000 € |

La différence est énorme! C'est pourquoi ce principe est fondamental.`,
        ru: `**Сценарий:** Машина куплена за 10 000 € со сроком службы 5 лет.

**При непрерывности деятельности:**
- Балансовая стоимость год 1: 10 000 - 2 000 = **8 000 €**
- Ежегодная амортизация: 2 000 € (10 000 / 5)

**Без непрерывности (ликвидация):**
- Немедленная стоимость перепродажи: **3 000 €** (рыночная цена б/у)

| Ситуация | Балансовая стоимость |
|----------|---------------------|
| Непрерывность | 8 000 € |
| Ликвидация | 3 000 € |

Разница огромна! Вот почему этот принцип является фундаментальным.`,
      },
      noteToRememberFr: 'L\'amortissement répartit le coût d\'un actif sur sa durée de vie utile, grâce au principe de continuité.',
      noteToRememberRu: 'Амортизация распределяет стоимость актива на срок его полезного использования благодаря принципу непрерывности.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Amortissement', ru: 'Упражнение: Амортизация' },
      question: {
        fr: 'Enregistrement de l\'amortissement annuel d\'un véhicule (2 000 €):',
        ru: 'Запись ежегодной амортизации транспортного средства (2 000 €):',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Dotation aux amortissements (charge)', ru: 'Амортизационные отчисления (расход)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Amortissements cumulés (actif diminue)', ru: 'Накопленная амортизация (актив уменьшается)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Principe de continuité', ru: 'Тест — Принцип непрерывности' },
      questions: [
        {
          id: 'l02-01-q1',
          question: { fr: 'Le principe de continuité suppose que l\'entreprise:', ru: 'Принцип непрерывности предполагает, что предприятие:' },
          options: [
            { id: 'a', text: { fr: 'Va fermer dans 1 an', ru: 'Закроется через 1 год' }, isCorrect: false },
            { id: 'b', text: { fr: 'Continue son activité dans un avenir prévisible', ru: 'Продолжит деятельность в обозримом будущем' }, isCorrect: true },
            { id: 'c', text: { fr: 'Va être vendue', ru: 'Будет продана' }, isCorrect: false },
            { id: 'd', text: { fr: 'Est en faillite', ru: 'Находится в банкротстве' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-01-q2',
          question: { fr: 'L\'amortissement est calculé sur:', ru: 'Амортизация рассчитывается на:' },
          options: [
            { id: 'a', text: { fr: 'La valeur de liquidation', ru: 'Ликвидационную стоимость' }, isCorrect: false },
            { id: 'b', text: { fr: 'La durée de vie du bien', ru: 'Срок службы имущества' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le prix du marché', ru: 'Рыночную цену' }, isCorrect: false },
            { id: 'd', text: { fr: 'La valeur fiscale', ru: 'Налоговую стоимость' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-01-q3',
          question: { fr: 'Quand utilise-t-on la valeur de liquidation?', ru: 'Когда используется ликвидационная стоимость?' },
          options: [
            { id: 'a', text: { fr: 'Toujours', ru: 'Всегда' }, isCorrect: false },
            { id: 'b', text: { fr: 'Jamais', ru: 'Никогда' }, isCorrect: false },
            { id: 'c', text: { fr: 'Quand l\'entreprise risque de fermer', ru: 'Когда предприятие рискует закрыться' }, isCorrect: true },
            { id: 'd', text: { fr: 'Chaque année', ru: 'Каждый год' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_02_02: LessonData = {
  id: 'l-02-02',
  title: { fr: 'Le principe de prudence', ru: 'Принцип осторожности' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Ne pas anticiper les profits', ru: 'Не предвосхищать прибыль' },
      content: {
        fr: `Le **principe de prudence** est fondamental en comptabilité. Il stipule:

- **Ne jamais anticiper** un profit qui n'est pas encore réalisé - **Enregistrer immédiatement** toute perte probable, même si elle n'est pas certaine
- En cas de doute, choisir la solution la **moins favorable** pour l'entreprise

**Applications pratiques:**
- Provisions pour créances douteuses (client qui risque de ne pas payer)
- Dépréciation des stocks (marchandises qui perdent de la valeur)
- Provisions pour risques et charges (litiges en cours)

Ce principe protège les **tiers** (banques, investisseurs) contre une présentation trop optimiste des comptes.`,
        ru: `**Принцип осторожности** является фундаментальным в бухгалтерии. Он гласит:

- **Никогда не предвосхищать** прибыль, которая ещё не реализована
- **Немедленно записывать** любой вероятный убыток, даже если он не является определённым
- В случае сомнений выбирать решение, **наименее благоприятное** для предприятия

**Практические применения:**
- Резервы по сомнительным долгам (клиент, который рискует не заплатить)
- Обесценение запасов (товары, теряющие стоимость)
- Резервы на риски и расходы (текущие судебные споры)

Этот принцип защищает **третьих лиц** (банки, инвесторов) от слишком оптимистичного представления счетов.`,
      },
      keyConceptFr: 'Mieux vaut être pessimiste en comptabilité! Le principe de prudence évite de présenter une image trop rose de l\'entreprise.',
      keyConceptRu: 'В бухгалтерии лучше быть пессимистом! Принцип осторожности позволяет избежать слишком радужного представления предприятия.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Provision pour créance douteuse', ru: 'Пример: Резерв по сомнительному долгу' },
      content: {
        fr: `**Scénario:** Un client doit 5 000 € mais est en difficulté financière.

**Application du principe de prudence:**
- On ne sait pas s'il va payer → risque de perte
- On crée une **provision** de 5 000 € pour couvrir ce risque

| Compte | Débit | Crédit |
|--------|-------|--------|
| Dotation aux provisions (charge) | 5 000 € | |
| Provision pour créances douteuses | | 5 000 € |

**Si le client paie finalement:** On reprend la provision (produit exceptionnel).
**Si le client ne paie pas:** On passe la créance en perte définitive.`,
        ru: `**Сценарий:** Клиент должен 5 000 €, но испытывает финансовые трудности.

**Применение принципа осторожности:**
- Неизвестно, заплатит ли он → риск убытка
- Создаём **резерв** в 5 000 € для покрытия этого риска

| Счёт | Дебет | Кредит |
|------|-------|--------|
| Отчисления в резервы (расход) | 5 000 € | |
| Резерв по сомнительным долгам | | 5 000 € |

**Если клиент в итоге платит:** Резерв восстанавливается (исключительный доход).
**Если клиент не платит:** Долг списывается как окончательный убыток.`,
      },
      noteToRememberFr: 'Une provision est une charge estimée pour couvrir un risque futur probable. Elle réduit le résultat de l\'exercice.',
      noteToRememberRu: 'Резерв — это расчётный расход для покрытия вероятного будущего риска. Он уменьшает результат периода.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Provision pour dépréciation', ru: 'Упражнение: Резерв на обесценение' },
      question: {
        fr: 'Des stocks valent 10 000 € mais leur valeur de marché est tombée à 7 000 €. Enregistrez la dépréciation:',
        ru: 'Запасы стоят 10 000 €, но их рыночная стоимость упала до 7 000 €. Запишите обесценение:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Dépréciation des stocks (charge)', ru: 'Обесценение запасов (расход)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Provision pour dépréciation des stocks', ru: 'Резерв на обесценение запасов' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Principe de prudence', ru: 'Тест — Принцип осторожности' },
      questions: [
        {
          id: 'l02-02-q1',
          question: { fr: 'Le principe de prudence dit de:', ru: 'Принцип осторожности говорит:' },
          options: [
            { id: 'a', text: { fr: 'Anticiper les profits futurs', ru: 'Предвосхищать будущие прибыли' }, isCorrect: false },
            { id: 'b', text: { fr: 'Enregistrer les pertes probables immédiatement', ru: 'Немедленно записывать вероятные убытки' }, isCorrect: true },
            { id: 'c', text: { fr: 'Ignorer les risques', ru: 'Игнорировать риски' }, isCorrect: false },
            { id: 'd', text: { fr: 'Surestimer les actifs', ru: 'Завышать активы' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-02-q2',
          question: { fr: 'Une provision est créée pour:', ru: 'Резерв создаётся для:' },
          options: [
            { id: 'a', text: { fr: 'Augmenter les profits', ru: 'Увеличения прибыли' }, isCorrect: false },
            { id: 'b', text: { fr: 'Couvrir un risque futur probable', ru: 'Покрытия вероятного будущего риска' }, isCorrect: true },
            { id: 'c', text: { fr: 'Réduire les impôts', ru: 'Снижения налогов' }, isCorrect: false },
            { id: 'd', text: { fr: 'Payer les salaires', ru: 'Выплаты зарплат' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-02-q3',
          question: { fr: 'En cas de doute, le comptable doit choisir:', ru: 'В случае сомнений бухгалтер должен выбрать:' },
          options: [
            { id: 'a', text: { fr: 'La solution la plus favorable', ru: 'Наиболее благоприятное решение' }, isCorrect: false },
            { id: 'b', text: { fr: 'La solution la moins favorable', ru: 'Наименее благоприятное решение' }, isCorrect: true },
            { id: 'c', text: { fr: 'La solution la plus simple', ru: 'Наиболее простое решение' }, isCorrect: false },
            { id: 'd', text: { fr: 'Ne rien enregistrer', ru: 'Ничего не записывать' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_02_03: LessonData = {
  id: 'l-02-03',
  title: { fr: "L'équation comptable", ru: 'Бухгалтерское уравнение' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Actif = Passif + Capitaux propres', ru: 'Актив = Пассив + Собственный капитал' },
      content: {
        fr: `L'**équation comptable fondamentale** est:

**ACTIF = PASSIF + CAPITAUX PROPRES**

- **Actif:** Ce que l'entreprise possède (machines, stocks, argent, créances)
- **Passif:** Ce que l'entreprise doit (emprunts, dettes fournisseurs)
- **Capitaux propres:** La valeur appartenant aux propriétaires (capital + résultats)

Cette équation est **toujours vraie** et ne peut jamais être déséquilibrée.

**Exemple:** Si l'actif = 100 000 €, et le passif = 60 000 €, alors les capitaux propres = 40 000 €.`,
        ru: `**Фундаментальное бухгалтерское уравнение**:

**АКТИВ = ПАССИВ + СОБСТВЕННЫЙ КАПИТАЛ**

- **Актив:** То, чем владеет предприятие (машины, запасы, деньги, дебиторская задолженность)
- **Пассив:** То, что должно предприятие (кредиты, долги поставщикам)
- **Собственный капитал:** Стоимость, принадлежащая владельцам (капитал + результаты)

Это уравнение **всегда верно** и никогда не может быть разбалансировано.

**Пример:** Если актив = 100 000 €, а пассив = 60 000 €, то собственный капитал = 40 000 €.`,
      },
      keyConceptFr: 'L\'équation comptable est le fondement de toute la comptabilité. Elle garantit que les comptes sont toujours équilibrés.',
      keyConceptRu: 'Бухгалтерское уравнение является основой всей бухгалтерии. Оно гарантирует, что счета всегда сбалансированы.',
    },
    {
      type: 'example',
      title: { fr: "Exemple: Vérifier l'équation", ru: 'Пример: Проверка уравнения' },
      content: {
        fr: `**Bilan simplifié d'une entreprise:**

| ACTIF | Montant | PASSIF + CP | Montant |
|-------|---------|-------------|---------|
| Machines | 50 000 € | Emprunt bancaire | 30 000 € |
| Stocks | 20 000 € | Dettes fournisseurs | 15 000 € |
| Banque | 15 000 € | Capital | 35 000 € |
| Clients | 10 000 € | Résultat | 15 000 € |
| **Total** | **95 000 €** | **Total** | **95 000 €** |

**Vérification:** 95 000 = 95 000 ✓ L'équation est respectée!`,
        ru: `**Упрощённый баланс предприятия:**

| АКТИВ | Сумма | ПАССИВ + СК | Сумма |
|-------|-------|-------------|-------|
| Машины | 50 000 € | Банковский кредит | 30 000 € |
| Запасы | 20 000 € | Долги поставщикам | 15 000 € |
| Банк | 15 000 € | Капитал | 35 000 € |
| Клиенты | 10 000 € | Результат | 15 000 € |
| **Итого** | **95 000 €** | **Итого** | **95 000 €** |

**Проверка:** 95 000 = 95 000 ✓ Уравнение соблюдено!`,
      },
      noteToRememberFr: 'Chaque transaction modifie au moins deux éléments du bilan, mais l\'équation reste toujours équilibrée.',
      noteToRememberRu: 'Каждая операция изменяет как минимум два элемента баланса, но уравнение всегда остаётся сбалансированным.',
    },
    {
      type: 'exercise',
      title: { fr: "Exercice: Équation comptable", ru: 'Упражнение: Бухгалтерское уравнение' },
      question: {
        fr: "L'entreprise emprunte 20 000 € à la banque. Classifiez les comptes:",
        ru: 'Предприятие берёт кредит 20 000 € в банке. Классифицируйте счета:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (argent reçu — actif augmente)', ru: 'Банк (полученные деньги — актив растёт)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Emprunt bancaire (dette — passif augmente)', ru: 'Банковский кредит (долг — пассив растёт)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: "Quiz — L'équation comptable", ru: 'Тест — Бухгалтерское уравнение' },
      questions: [
        {
          id: 'l02-03-q1',
          question: { fr: "L'équation comptable fondamentale est:", ru: 'Фундаментальное бухгалтерское уравнение:' },
          options: [
            { id: 'a', text: { fr: 'Actif = Passif - Capitaux propres', ru: 'Актив = Пассив - Собственный капитал' }, isCorrect: false },
            { id: 'b', text: { fr: 'Actif = Passif + Capitaux propres', ru: 'Актив = Пассив + Собственный капитал' }, isCorrect: true },
            { id: 'c', text: { fr: 'Passif = Actif + Capitaux propres', ru: 'Пассив = Актив + Собственный капитал' }, isCorrect: false },
            { id: 'd', text: { fr: 'Capitaux propres = Actif + Passif', ru: 'Собственный капитал = Актив + Пассив' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-03-q2',
          question: { fr: "L'actif comprend:", ru: 'Актив включает:' },
          options: [
            { id: 'a', text: { fr: 'Les dettes de l\'entreprise', ru: 'Долги предприятия' }, isCorrect: false },
            { id: 'b', text: { fr: 'Ce que l\'entreprise possède', ru: 'То, чем владеет предприятие' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le capital des actionnaires', ru: 'Капитал акционеров' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les charges de l\'exercice', ru: 'Расходы периода' }, isCorrect: false },
          ],
        },
        {
          id: 'l02-03-q3',
          question: { fr: 'Si Actif = 80 000 € et Passif = 50 000 €, les Capitaux propres sont:', ru: 'Если Актив = 80 000 € и Пассив = 50 000 €, то Собственный капитал:' },
          options: [
            { id: 'a', text: { fr: '130 000 €', ru: '130 000 €' }, isCorrect: false },
            { id: 'b', text: { fr: '30 000 €', ru: '30 000 €' }, isCorrect: true },
            { id: 'c', text: { fr: '50 000 €', ru: '50 000 €' }, isCorrect: false },
            { id: 'd', text: { fr: '80 000 €', ru: '80 000 €' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 3 LESSONS ─────────────────────────────────────────────────────────

const l_03_01: LessonData = {
  id: 'l-03-01',
  title: { fr: 'Comprendre le débit', ru: 'Понимание дебета' },
  steps: [
    {
      type: 'theory',
      title: { fr: "Qu'est-ce que le débit?", ru: 'Что такое дебет?' },
      content: {
        fr: `Le **débit** est l'un des deux côtés d'une écriture comptable (l'autre étant le crédit).

**Règle du débit:**
- Augmente les **actifs** (ce que l'entreprise possède)
- Augmente les **charges** (dépenses de l'entreprise)
- Diminue les **passifs** (dettes)
- Diminue les **capitaux propres**
- Diminue les **produits** (revenus)

Le débit est toujours inscrit à **gauche** dans un compte en T.

**Moyen mnémotechnique:** DEAD — Dépenses, Expenses, Assets, Drawings augmentent au Débit.`,
        ru: `**Дебет** — одна из двух сторон бухгалтерской записи (другая — кредит).

**Правило дебета:**
- Увеличивает **активы** (то, чем владеет предприятие)
- Увеличивает **расходы** (затраты предприятия)
- Уменьшает **пассивы** (долги)
- Уменьшает **собственный капитал**
- Уменьшает **доходы**

Дебет всегда записывается **слева** в Т-счёте.

**Мнемоника:** Дебет увеличивает Активы и Расходы.`,
      },
      keyConceptFr: 'Le débit est à gauche. Il augmente les actifs et les charges, et diminue les passifs et les produits.',
      keyConceptRu: 'Дебет — слева. Он увеличивает активы и расходы, уменьшает пассивы и доходы.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Compte en T', ru: 'Пример: Т-счёт' },
      content: {
        fr: `**Compte Caisse (actif):**

| Débit (gauche) | Crédit (droite) |
|----------------|-----------------|
| +500 € (reçu) | -200 € (payé) |
| +300 € (reçu) | |
| **Solde: +600 €** | |

**Interprétation:** La caisse a reçu 800 € et payé 200 €. Le solde débiteur est 600 €.

Pour un compte d'actif, un **solde débiteur** signifie que l'actif a de la valeur.`,
        ru: `**Счёт Касса (актив):**

| Дебет (слева) | Кредит (справа) |
|---------------|-----------------|
| +500 € (получено) | -200 € (уплачено) |
| +300 € (получено) | |
| **Остаток: +600 €** | |

**Интерпретация:** Касса получила 800 € и уплатила 200 €. Дебетовый остаток — 600 €.

Для счёта актива **дебетовый остаток** означает, что актив имеет стоимость.`,
      },
      noteToRememberFr: 'Pour les comptes d\'actif et de charges, un solde débiteur est normal. Pour les passifs et produits, c\'est un solde créditeur.',
      noteToRememberRu: 'Для счетов актива и расходов нормален дебетовый остаток. Для пассивов и доходов — кредитовый.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Identifier les débits', ru: 'Упражнение: Определите дебеты' },
      question: {
        fr: "L'entreprise achète un ordinateur 1 200 € en espèces. Classifiez:",
        ru: 'Предприятие покупает компьютер за 1 200 € наличными. Классифицируйте:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Matériel informatique (actif augmente)', ru: 'Компьютерное оборудование (актив растёт)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Caisse (actif diminue)', ru: 'Касса (актив уменьшается)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Le débit', ru: 'Тест — Дебет' },
      questions: [
        {
          id: 'l03-01-q1',
          question: { fr: 'Le débit augmente:', ru: 'Дебет увеличивает:' },
          options: [
            { id: 'a', text: { fr: 'Les passifs et les produits', ru: 'Пассивы и доходы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les actifs et les charges', ru: 'Активы и расходы' }, isCorrect: true },
            { id: 'c', text: { fr: 'Seulement les actifs', ru: 'Только активы' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les capitaux propres', ru: 'Собственный капитал' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-01-q2',
          question: { fr: 'Dans un compte en T, le débit est:', ru: 'В Т-счёте дебет находится:' },
          options: [
            { id: 'a', text: { fr: 'À droite', ru: 'Справа' }, isCorrect: false },
            { id: 'b', text: { fr: 'À gauche', ru: 'Слева' }, isCorrect: true },
            { id: 'c', text: { fr: 'Au centre', ru: 'В центре' }, isCorrect: false },
            { id: 'd', text: { fr: 'En bas', ru: 'Внизу' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-01-q3',
          question: { fr: 'Débiter un compte de charges signifie:', ru: 'Дебетовать счёт расходов означает:' },
          options: [
            { id: 'a', text: { fr: 'Diminuer les charges', ru: 'Уменьшить расходы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Augmenter les charges', ru: 'Увеличить расходы' }, isCorrect: true },
            { id: 'c', text: { fr: 'Annuler les charges', ru: 'Аннулировать расходы' }, isCorrect: false },
            { id: 'd', text: { fr: 'Transférer les charges', ru: 'Перенести расходы' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_03_02: LessonData = {
  id: 'l-03-02',
  title: { fr: 'Comprendre le crédit', ru: 'Понимание кредита' },
  steps: [
    {
      type: 'theory',
      title: { fr: "Qu'est-ce que le crédit?", ru: 'Что такое кредит?' },
      content: {
        fr: `Le **crédit** est le côté droit d'une écriture comptable.

**Règle du crédit:**
- Augmente les **passifs** (dettes de l'entreprise)
- Augmente les **capitaux propres**
- Augmente les **produits** (revenus)
- Diminue les **actifs**
- Diminue les **charges**

Le crédit est toujours inscrit à **droite** dans un compte en T.

**Moyen mnémotechnique:** GIRLS — Gains, Income, Revenues, Liabilities, Stockholders' equity augmentent au Crédit.`,
        ru: `**Кредит** — правая сторона бухгалтерской записи.

**Правило кредита:**
- Увеличивает **пассивы** (долги предприятия)
- Увеличивает **собственный капитал**
- Увеличивает **доходы**
- Уменьшает **активы**
- Уменьшает **расходы**

Кредит всегда записывается **справа** в Т-счёте.

**Мнемоника:** Кредит увеличивает Пассивы, Капитал и Доходы.`,
      },
      keyConceptFr: 'Le crédit est à droite. Il augmente les passifs, les capitaux propres et les produits, et diminue les actifs et les charges.',
      keyConceptRu: 'Кредит — справа. Он увеличивает пассивы, собственный капитал и доходы, уменьшает активы и расходы.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Vente à crédit', ru: 'Пример: Продажа в кредит' },
      content: {
        fr: `**Scénario:** L'entreprise vend des marchandises pour 2 000 € à crédit (le client paiera plus tard).

| Compte | Débit | Crédit |
|--------|-------|--------|
| Créances clients (actif ↑) | 2 000 € | |
| Ventes (produit ↑) | | 2 000 € |

**Analyse:**
- Les créances clients augmentent → **débit** (actif)
- Les ventes augmentent → **crédit** (produit)
- Équilibre: 2 000 = 2 000 ✓`,
        ru: `**Сценарий:** Предприятие продаёт товары на 2 000 € в кредит (клиент заплатит позже).

| Счёт | Дебет | Кредит |
|------|-------|--------|
| Дебиторская задолженность (актив ↑) | 2 000 € | |
| Продажи (доход ↑) | | 2 000 € |

**Анализ:**
- Дебиторская задолженность растёт → **дебет** (актив)
- Продажи растут → **кредит** (доход)
- Баланс: 2 000 = 2 000 ✓`,
      },
      noteToRememberFr: 'Crédit ne signifie pas "argent reçu" en comptabilité! C\'est simplement le côté droit d\'une écriture.',
      noteToRememberRu: 'Кредит в бухгалтерии не означает "полученные деньги"! Это просто правая сторона записи.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Identifier les crédits', ru: 'Упражнение: Определите кредиты' },
      question: {
        fr: "L'entreprise contracte un emprunt de 10 000 €. Classifiez:",
        ru: 'Предприятие берёт кредит на 10 000 €. Классифицируйте:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (actif augmente)', ru: 'Банк (актив растёт)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Emprunt (passif augmente)', ru: 'Кредит (пассив растёт)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Le crédit', ru: 'Тест — Кредит' },
      questions: [
        {
          id: 'l03-02-q1',
          question: { fr: 'Le crédit augmente:', ru: 'Кредит увеличивает:' },
          options: [
            { id: 'a', text: { fr: 'Les actifs et les charges', ru: 'Активы и расходы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les passifs et les produits', ru: 'Пассивы и доходы' }, isCorrect: true },
            { id: 'c', text: { fr: 'Seulement les passifs', ru: 'Только пассивы' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les charges uniquement', ru: 'Только расходы' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-02-q2',
          question: { fr: 'Dans un compte en T, le crédit est:', ru: 'В Т-счёте кредит находится:' },
          options: [
            { id: 'a', text: { fr: 'À gauche', ru: 'Слева' }, isCorrect: false },
            { id: 'b', text: { fr: 'À droite', ru: 'Справа' }, isCorrect: true },
            { id: 'c', text: { fr: 'Au milieu', ru: 'Посередине' }, isCorrect: false },
            { id: 'd', text: { fr: 'En haut', ru: 'Вверху' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-02-q3',
          question: { fr: 'Créditer un compte de ventes signifie:', ru: 'Кредитовать счёт продаж означает:' },
          options: [
            { id: 'a', text: { fr: 'Diminuer les ventes', ru: 'Уменьшить продажи' }, isCorrect: false },
            { id: 'b', text: { fr: 'Augmenter les ventes', ru: 'Увеличить продажи' }, isCorrect: true },
            { id: 'c', text: { fr: 'Annuler les ventes', ru: 'Аннулировать продажи' }, isCorrect: false },
            { id: 'd', text: { fr: 'Rembourser les ventes', ru: 'Возместить продажи' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_03_03: LessonData = {
  id: 'l-03-03',
  title: { fr: 'La règle du débit-crédit', ru: 'Правило дебет-кредит' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'La règle fondamentale', ru: 'Фундаментальное правило' },
      content: {
        fr: `La comptabilité en partie double est basée sur une règle simple: **chaque transaction affecte au moins deux comptes**.

La règle est: **Débit = Crédit**

Pour chaque écriture comptable:
- Le total des débits doit toujours égaler le total des crédits
- Un débit augmente les actifs et les charges
- Un crédit augmente les passifs, les capitaux propres et les produits`,
        ru: `Двойная бухгалтерия основана на простом правиле: **каждая транзакция затрагивает как минимум два счета**.

Правило: **Дебет = Кредит**

Для каждой бухгалтерской записи:
- Сумма дебетов всегда должна равняться сумме кредитов
- Дебет увеличивает активы и расходы
- Кредит увеличивает пассивы, собственный капитал и доходы`,
      },
      keyConceptFr: 'La partie double garantit que chaque transaction est enregistrée de manière équilibrée. Débit = Crédit toujours.',
      keyConceptRu: 'Двойная запись гарантирует, что каждая транзакция записывается сбалансированно. Дебет = Кредит всегда.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple pratique', ru: 'Практический пример' },
      content: {
        fr: `**Exemple:** L'entreprise achète du matériel pour 1 000 €, payé en espèces.

| Compte | Débit | Crédit |
|--------|-------|--------|
| Matériel (Actif ↑) | 1 000 € | |
| Caisse (Actif ↓) | | 1 000 € |

**Analyse:** Le matériel augmente (débit), la caisse diminue (crédit). L'équation reste équilibrée.`,
        ru: `**Пример:** Компания покупает оборудование за 1 000 €, оплата наличными.

| Счет | Дебет | Кредит |
|------|-------|--------|
| Оборудование (Актив ↑) | 1 000 € | |
| Касса (Актив ↓) | | 1 000 € |

**Анализ:** Оборудование увеличивается (дебет), касса уменьшается (кредит). Уравнение остается сбалансированным.`,
      },
      noteToRememberFr: "L'équation comptable Actif = Passif + Capitaux propres doit toujours rester équilibrée.",
      noteToRememberRu: 'Бухгалтерское уравнение Актив = Пассив + Собственный капитал всегда должно оставаться сбалансированным.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Classifiez les comptes', ru: 'Упражнение: Классифицируйте счета' },
      question: {
        fr: "L'entreprise reçoit un paiement client de 500 €. Classifiez les mouvements:",
        ru: 'Компания получает платеж от клиента на 500 €. Классифицируйте движения:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Caisse', ru: 'Касса' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Créances clients', ru: 'Дебиторская задолженность' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz de validation', ru: 'Контрольный тест' },
      questions: [
        {
          id: 'l03-03-q1',
          question: { fr: "Que se passe-t-il lorsqu'on débite un compte d'actif?", ru: 'Что происходит при дебетовании счета актива?' },
          options: [
            { id: 'a', text: { fr: 'Il diminue', ru: 'Он уменьшается' }, isCorrect: false },
            { id: 'b', text: { fr: 'Il augmente', ru: 'Он увеличивается' }, isCorrect: true },
            { id: 'c', text: { fr: 'Il reste inchangé', ru: 'Он остается неизменным' }, isCorrect: false },
            { id: 'd', text: { fr: 'Il est supprimé', ru: 'Он удаляется' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-03-q2',
          question: { fr: "Dans une écriture comptable, le total des débits doit:", ru: 'В бухгалтерской записи сумма дебетов должна:' },
          options: [
            { id: 'a', text: { fr: 'Être supérieur aux crédits', ru: 'Превышать кредиты' }, isCorrect: false },
            { id: 'b', text: { fr: 'Être inférieur aux crédits', ru: 'Быть меньше кредитов' }, isCorrect: false },
            { id: 'c', text: { fr: 'Être égal aux crédits', ru: 'Быть равна кредитам' }, isCorrect: true },
            { id: 'd', text: { fr: "N'avoir aucun lien avec les crédits", ru: 'Не иметь связи с кредитами' }, isCorrect: false },
          ],
        },
        {
          id: 'l03-03-q3',
          question: { fr: 'Un crédit sur un compte de passif:', ru: 'Кредит на счете пассива:' },
          options: [
            { id: 'a', text: { fr: 'Diminue le passif', ru: 'Уменьшает пассив' }, isCorrect: false },
            { id: 'b', text: { fr: 'Augmente le passif', ru: 'Увеличивает пассив' }, isCorrect: true },
            { id: 'c', text: { fr: "N'affecte pas le passif", ru: 'Не влияет на пассив' }, isCorrect: false },
            { id: 'd', text: { fr: 'Annule le passif', ru: 'Аннулирует пассив' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 4 LESSONS ─────────────────────────────────────────────────────────

const l_04_01: LessonData = {
  id: 'l-04-01',
  title: { fr: 'Introduction aux écritures', ru: 'Введение в записи' },
  steps: [
    {
      type: 'theory',
      title: { fr: "Structure d'une écriture comptable", ru: 'Структура бухгалтерской записи' },
      content: {
        fr: `Une **écriture comptable** est l'enregistrement d'une transaction dans le journal.

**Structure obligatoire:**
1. **Date** de la transaction
2. **Numéro de compte** (plan comptable général)
3. **Libellé** (description de l'opération)
4. **Montant au débit** ou **montant au crédit**

**Le Plan Comptable Général (PCG)** organise les comptes en classes:
- Classe 1: Comptes de capitaux
- Classe 2: Comptes d'immobilisations
- Classe 3: Comptes de stocks
- Classe 4: Comptes de tiers (clients, fournisseurs)
- Classe 5: Comptes financiers (banque, caisse)
- Classe 6: Comptes de charges
- Classe 7: Comptes de produits`,
        ru: `**Бухгалтерская запись** — это регистрация операции в журнале.

**Обязательная структура:**
1. **Дата** операции
2. **Номер счёта** (общий план счетов)
3. **Описание** (описание операции)
4. **Сумма по дебету** или **сумма по кредиту**

**Общий план счетов (PCG)** организует счета по классам:
- Класс 1: Счета капитала
- Класс 2: Счета основных средств
- Класс 3: Счета запасов
- Класс 4: Счета третьих лиц (клиенты, поставщики)
- Класс 5: Финансовые счета (банк, касса)
- Класс 6: Счета расходов
- Класс 7: Счета доходов`,
      },
      keyConceptFr: 'Chaque écriture doit avoir un débit égal au crédit. La date et le libellé sont obligatoires.',
      keyConceptRu: 'Каждая запись должна иметь дебет, равный кредиту. Дата и описание обязательны.',
    },
    {
      type: 'example',
      title: { fr: "Exemple d'écriture complète", ru: 'Пример полной записи' },
      content: {
        fr: `**Transaction:** Achat de marchandises 3 000 € payé par banque le 15/03/2026.

| Date | Compte | Libellé | Débit | Crédit |
|------|--------|---------|-------|--------|
| 15/03 | 607 | Achats de marchandises | 3 000 € | |
| 15/03 | 512 | Banque | | 3 000 € |

**Explication:**
- Compte 607 (Achats) → débité car charge augmente
- Compte 512 (Banque) → crédité car actif diminue`,
        ru: `**Операция:** Покупка товаров на 3 000 € оплачена через банк 15/03/2026.

| Дата | Счёт | Описание | Дебет | Кредит |
|------|------|----------|-------|--------|
| 15/03 | 607 | Покупка товаров | 3 000 € | |
| 15/03 | 512 | Банк | | 3 000 € |

**Объяснение:**
- Счёт 607 (Покупки) → дебетуется, т.к. расход растёт
- Счёт 512 (Банк) → кредитуется, т.к. актив уменьшается`,
      },
      noteToRememberFr: 'Les numéros de compte du PCG permettent de classer et retrouver facilement toutes les transactions.',
      noteToRememberRu: 'Номера счетов PCG позволяют легко классифицировать и находить все операции.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Écriture de vente', ru: 'Упражнение: Запись продажи' },
      question: {
        fr: "Vente de marchandises 5 000 € encaissée en banque. Classifiez:",
        ru: 'Продажа товаров на 5 000 €, зачислено в банк. Классифицируйте:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (actif augmente)', ru: 'Банк (актив растёт)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Ventes de marchandises (produit)', ru: 'Продажи товаров (доход)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Écritures comptables', ru: 'Тест — Бухгалтерские записи' },
      questions: [
        {
          id: 'l04-01-q1',
          question: { fr: 'Les comptes de classe 6 sont:', ru: 'Счета класса 6 — это:' },
          options: [
            { id: 'a', text: { fr: 'Des comptes de produits', ru: 'Счета доходов' }, isCorrect: false },
            { id: 'b', text: { fr: 'Des comptes de charges', ru: 'Счета расходов' }, isCorrect: true },
            { id: 'c', text: { fr: 'Des comptes de tiers', ru: 'Счета третьих лиц' }, isCorrect: false },
            { id: 'd', text: { fr: 'Des comptes financiers', ru: 'Финансовые счета' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-01-q2',
          question: { fr: 'Le compte 512 correspond à:', ru: 'Счёт 512 соответствует:' },
          options: [
            { id: 'a', text: { fr: 'Les achats', ru: 'Покупкам' }, isCorrect: false },
            { id: 'b', text: { fr: 'La banque', ru: 'Банку' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les ventes', ru: 'Продажам' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les stocks', ru: 'Запасам' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-01-q3',
          question: { fr: "Qu'est-ce qu'un libellé dans une écriture?", ru: 'Что такое описание в записи?' },
          options: [
            { id: 'a', text: { fr: 'Le numéro de compte', ru: 'Номер счёта' }, isCorrect: false },
            { id: 'b', text: { fr: 'La description de la transaction', ru: 'Описание операции' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le montant total', ru: 'Общая сумма' }, isCorrect: false },
            { id: 'd', text: { fr: 'La date de paiement', ru: 'Дата оплаты' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_04_02: LessonData = {
  id: 'l-04-02',
  title: { fr: 'Écritures de vente', ru: 'Записи продаж' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Enregistrer une vente', ru: 'Запись продажи' },
      content: {
        fr: `L'enregistrement d'une **vente** dépend du mode de paiement:

**Vente au comptant (paiement immédiat):**
- Débit: Banque ou Caisse (actif augmente)
- Crédit: Ventes (produit augmente)

**Vente à crédit (paiement différé):**
- Débit: Créances clients (actif augmente)
- Crédit: Ventes (produit augmente)

**Encaissement d'une créance:**
- Débit: Banque (actif augmente)
- Crédit: Créances clients (actif diminue)

La TVA doit également être enregistrée séparément (voir niveau 6).`,
        ru: `Запись **продажи** зависит от способа оплаты:

**Продажа за наличные (немедленная оплата):**
- Дебет: Банк или Касса (актив растёт)
- Кредит: Продажи (доход растёт)

**Продажа в кредит (отложенная оплата):**
- Дебет: Дебиторская задолженность (актив растёт)
- Кредит: Продажи (доход растёт)

**Получение дебиторской задолженности:**
- Дебет: Банк (актив растёт)
- Кредит: Дебиторская задолженность (актив уменьшается)

НДС также должен записываться отдельно (см. уровень 6).`,
      },
      keyConceptFr: 'Une vente génère toujours un produit (crédit). La contrepartie est soit la banque, soit une créance client.',
      keyConceptRu: 'Продажа всегда генерирует доход (кредит). Контрпроводка — либо банк, либо дебиторская задолженность.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Vente à crédit puis encaissement', ru: 'Пример: Продажа в кредит и получение оплаты' },
      content: {
        fr: `**Étape 1:** Vente à crédit de 4 000 € le 01/04.

| Compte | Débit | Crédit |
|--------|-------|--------|
| 411 Clients | 4 000 € | |
| 707 Ventes | | 4 000 € |

**Étape 2:** Encaissement le 15/04.

| Compte | Débit | Crédit |
|--------|-------|--------|
| 512 Banque | 4 000 € | |
| 411 Clients | | 4 000 € |`,
        ru: `**Шаг 1:** Продажа в кредит на 4 000 € 01/04.

| Счёт | Дебет | Кредит |
|------|-------|--------|
| 411 Клиенты | 4 000 € | |
| 707 Продажи | | 4 000 € |

**Шаг 2:** Получение оплаты 15/04.

| Счёт | Дебет | Кредит |
|------|-------|--------|
| 512 Банк | 4 000 € | |
| 411 Клиенты | | 4 000 € |`,
      },
      noteToRememberFr: 'Le compte 411 (Clients) est un compte d\'actif. Il augmente lors de la vente et diminue lors de l\'encaissement.',
      noteToRememberRu: 'Счёт 411 (Клиенты) — счёт актива. Он растёт при продаже и уменьшается при получении оплаты.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Vente comptant', ru: 'Упражнение: Продажа за наличные' },
      question: {
        fr: 'Vente de services 2 500 € payée immédiatement en banque:',
        ru: 'Продажа услуг на 2 500 €, оплата немедленно в банк:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (512)', ru: 'Банк (512)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Prestations de services (706)', ru: 'Услуги (706)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Écritures de vente', ru: 'Тест — Записи продаж' },
      questions: [
        {
          id: 'l04-02-q1',
          question: { fr: 'Lors d\'une vente à crédit, on débite:', ru: 'При продаже в кредит дебетуется:' },
          options: [
            { id: 'a', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les créances clients', ru: 'Дебиторская задолженность' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les charges', ru: 'Расходы' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-02-q2',
          question: { fr: 'Le compte 411 représente:', ru: 'Счёт 411 представляет:' },
          options: [
            { id: 'a', text: { fr: 'Les fournisseurs', ru: 'Поставщиков' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les clients', ru: 'Клиентов' }, isCorrect: true },
            { id: 'c', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-02-q3',
          question: { fr: 'Lors de l\'encaissement d\'une créance client, on crédite:', ru: 'При получении дебиторской задолженности кредитуется:' },
          options: [
            { id: 'a', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
            { id: 'c', text: { fr: 'Les créances clients', ru: 'Дебиторская задолженность' }, isCorrect: true },
            { id: 'd', text: { fr: 'Les charges', ru: 'Расходы' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_04_03: LessonData = {
  id: 'l-04-03',
  title: { fr: "Écritures d'achat", ru: 'Записи покупок' },
  steps: [
    {
      type: 'theory',
      title: { fr: "Enregistrer un achat", ru: 'Запись покупки' },
      content: {
        fr: `L'enregistrement d'un **achat** suit la même logique que les ventes, mais en sens inverse:

**Achat au comptant:**
- Débit: Achats (charge augmente)
- Crédit: Banque (actif diminue)

**Achat à crédit:**
- Débit: Achats (charge augmente)
- Crédit: Dettes fournisseurs (passif augmente)

**Paiement d'une dette fournisseur:**
- Débit: Dettes fournisseurs (passif diminue)
- Crédit: Banque (actif diminue)

Le compte **401 Fournisseurs** enregistre toutes les dettes envers les fournisseurs.`,
        ru: `Запись **покупки** следует той же логике, что и продажи, но в обратном направлении:

**Покупка за наличные:**
- Дебет: Покупки (расход растёт)
- Кредит: Банк (актив уменьшается)

**Покупка в кредит:**
- Дебет: Покупки (расход растёт)
- Кредит: Долги поставщикам (пассив растёт)

**Оплата долга поставщику:**
- Дебет: Долги поставщикам (пассив уменьшается)
- Кредит: Банк (актив уменьшается)

Счёт **401 Поставщики** записывает все долги поставщикам.`,
      },
      keyConceptFr: 'Le compte 401 (Fournisseurs) est un compte de passif. Il augmente lors de l\'achat à crédit et diminue lors du paiement.',
      keyConceptRu: 'Счёт 401 (Поставщики) — счёт пассива. Он растёт при покупке в кредит и уменьшается при оплате.',
    },
    {
      type: 'example',
      title: { fr: "Exemple: Achat à crédit puis paiement", ru: 'Пример: Покупка в кредит и оплата' },
      content: {
        fr: `**Étape 1:** Achat de marchandises à crédit 6 000 € le 05/05.

| Compte | Débit | Crédit |
|--------|-------|--------|
| 607 Achats | 6 000 € | |
| 401 Fournisseurs | | 6 000 € |

**Étape 2:** Paiement du fournisseur le 20/05.

| Compte | Débit | Crédit |
|--------|-------|--------|
| 401 Fournisseurs | 6 000 € | |
| 512 Banque | | 6 000 € |`,
        ru: `**Шаг 1:** Покупка товаров в кредит на 6 000 € 05/05.

| Счёт | Дебет | Кредит |
|------|-------|--------|
| 607 Покупки | 6 000 € | |
| 401 Поставщики | | 6 000 € |

**Шаг 2:** Оплата поставщику 20/05.

| Счёт | Дебет | Кредит |
|------|-------|--------|
| 401 Поставщики | 6 000 € | |
| 512 Банк | | 6 000 € |`,
      },
      noteToRememberFr: 'Lors du paiement d\'un fournisseur, on débite le compte fournisseur (passif diminue) et on crédite la banque (actif diminue).',
      noteToRememberRu: 'При оплате поставщику дебетуется счёт поставщика (пассив уменьшается) и кредитуется банк (актив уменьшается).',
    },
    {
      type: 'exercise',
      title: { fr: "Exercice: Achat à crédit", ru: 'Упражнение: Покупка в кредит' },
      question: {
        fr: "Achat de fournitures 800 € à crédit fournisseur:",
        ru: 'Покупка расходных материалов на 800 € в кредит поставщику:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Achats de fournitures (606)', ru: 'Покупка расходных материалов (606)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Fournisseurs (401)', ru: 'Поставщики (401)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: "Quiz — Écritures d'achat", ru: "Тест — Записи покупок" },
      questions: [
        {
          id: 'l04-03-q1',
          question: { fr: "Lors d'un achat à crédit, on crédite:", ru: 'При покупке в кредит кредитуется:' },
          options: [
            { id: 'a', text: { fr: 'Les achats', ru: 'Покупки' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les dettes fournisseurs', ru: 'Долги поставщикам' }, isCorrect: true },
            { id: 'c', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-03-q2',
          question: { fr: 'Le compte 401 représente:', ru: 'Счёт 401 представляет:' },
          options: [
            { id: 'a', text: { fr: 'Les clients', ru: 'Клиентов' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les fournisseurs', ru: 'Поставщиков' }, isCorrect: true },
            { id: 'c', text: { fr: 'La caisse', ru: 'Кассу' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les emprunts', ru: 'Кредиты' }, isCorrect: false },
          ],
        },
        {
          id: 'l04-03-q3',
          question: { fr: 'Lors du paiement d\'un fournisseur, le compte fournisseur est:', ru: 'При оплате поставщику счёт поставщика:' },
          options: [
            { id: 'a', text: { fr: 'Crédité', ru: 'Кредитуется' }, isCorrect: false },
            { id: 'b', text: { fr: 'Débité', ru: 'Дебетуется' }, isCorrect: true },
            { id: 'c', text: { fr: 'Annulé', ru: 'Аннулируется' }, isCorrect: false },
            { id: 'd', text: { fr: 'Inchangé', ru: 'Не изменяется' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 5 LESSONS ─────────────────────────────────────────────────────────

const l_05_01: LessonData = {
  id: 'l-05-01',
  title: { fr: "Structure d'une facture", ru: 'Структура счёта-фактуры' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Les éléments obligatoires', ru: 'Обязательные элементы' },
      content: {
        fr: `Une **facture** est un document commercial et comptable obligatoire. Elle doit contenir:

**Mentions obligatoires:**
1. Numéro de facture (séquentiel)
2. Date d'émission
3. Nom et adresse du vendeur + numéro SIRET
4. Nom et adresse de l'acheteur
5. Description des biens/services
6. Quantité et prix unitaire
7. Montant HT (Hors Taxes)
8. Taux de TVA applicable
9. Montant de TVA
10. Montant TTC (Toutes Taxes Comprises)
11. Conditions de paiement

**Importance légale:** Une facture sans ces mentions peut être rejetée par l'administration fiscale.`,
        ru: `**Счёт-фактура** — обязательный коммерческий и бухгалтерский документ. Он должен содержать:

**Обязательные реквизиты:**
1. Номер счёта-фактуры (последовательный)
2. Дата выставления
3. Наименование и адрес продавца + номер SIRET
4. Наименование и адрес покупателя
5. Описание товаров/услуг
6. Количество и цена за единицу
7. Сумма без НДС (HT)
8. Применяемая ставка НДС
9. Сумма НДС
10. Сумма с НДС (TTC)
11. Условия оплаты

**Юридическое значение:** Счёт-фактура без этих реквизитов может быть отклонён налоговой администрацией.`,
      },
      keyConceptFr: 'La facture est la pièce justificative principale de toute transaction commerciale. Elle doit être conservée 10 ans.',
      keyConceptRu: 'Счёт-фактура — основной оправдательный документ любой коммерческой операции. Он должен храниться 10 лет.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Facture simplifiée', ru: 'Пример: Упрощённый счёт-фактура' },
      content: {
        fr: `**FACTURE N° 2026-042**

Vendeur: Vika Comptable SARL | SIRET: 123 456 789 00012
Acheteur: Client ABC | Date: 15/03/2026

| Désignation | Qté | Prix HT | Total HT |
|-------------|-----|---------|----------|
| Formation comptabilité | 1 | 500 € | 500 € |
| Support de cours | 2 | 25 € | 50 € |

Sous-total HT: **550 €**
TVA 20%: **110 €**
**Total TTC: 660 €**

Paiement à 30 jours.`,
        ru: `**СЧЁТ-ФАКТУРА № 2026-042**

Продавец: Vika Comptable SARL | SIRET: 123 456 789 00012
Покупатель: Клиент ABC | Дата: 15/03/2026

| Наименование | Кол-во | Цена без НДС | Итого без НДС |
|--------------|--------|--------------|---------------|
| Обучение бухгалтерии | 1 | 500 € | 500 € |
| Учебные материалы | 2 | 25 € | 50 € |

Итого без НДС: **550 €**
НДС 20%: **110 €**
**Итого с НДС: 660 €**

Оплата в течение 30 дней.`,
      },
      noteToRememberFr: 'HT = Hors Taxes (sans TVA). TTC = Toutes Taxes Comprises (avec TVA). TTC = HT × (1 + taux TVA).',
      noteToRememberRu: 'HT = без НДС. TTC = с НДС. TTC = HT × (1 + ставка НДС).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Enregistrer une facture achat', ru: 'Упражнение: Запись счёта покупки' },
      question: {
        fr: 'Réception facture fournisseur 1 000 € HT + TVA 200 €. Enregistrez l\'achat HT:',
        ru: 'Получен счёт поставщика 1 000 € без НДС + НДС 200 €. Запишите покупку без НДС:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Achats (607) — montant HT', ru: 'Покупки (607) — сумма без НДС' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Fournisseurs (401) — montant TTC', ru: 'Поставщики (401) — сумма с НДС' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Structure de facture', ru: 'Тест — Структура счёта-фактуры' },
      questions: [
        {
          id: 'l05-01-q1',
          question: { fr: 'TTC signifie:', ru: 'TTC означает:' },
          options: [
            { id: 'a', text: { fr: 'Toutes Taxes Comprises', ru: 'Все налоги включены' }, isCorrect: true },
            { id: 'b', text: { fr: 'Taxe Totale Calculée', ru: 'Общий рассчитанный налог' }, isCorrect: false },
            { id: 'c', text: { fr: 'Taux de TVA Calculé', ru: 'Рассчитанная ставка НДС' }, isCorrect: false },
            { id: 'd', text: { fr: 'Total Taxe Comptable', ru: 'Общий бухгалтерский налог' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-01-q2',
          question: { fr: 'Combien de temps faut-il conserver une facture?', ru: 'Сколько лет нужно хранить счёт-фактуру?' },
          options: [
            { id: 'a', text: { fr: '3 ans', ru: '3 года' }, isCorrect: false },
            { id: 'b', text: { fr: '5 ans', ru: '5 лет' }, isCorrect: false },
            { id: 'c', text: { fr: '10 ans', ru: '10 лет' }, isCorrect: true },
            { id: 'd', text: { fr: '1 an', ru: '1 год' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-01-q3',
          question: { fr: 'Le SIRET est:', ru: 'SIRET — это:' },
          options: [
            { id: 'a', text: { fr: 'Un numéro de compte bancaire', ru: 'Номер банковского счёта' }, isCorrect: false },
            { id: 'b', text: { fr: "Un identifiant unique de l'entreprise", ru: 'Уникальный идентификатор предприятия' }, isCorrect: true },
            { id: 'c', text: { fr: 'Un code postal', ru: 'Почтовый индекс' }, isCorrect: false },
            { id: 'd', text: { fr: 'Un numéro de TVA', ru: 'Номер НДС' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_05_02: LessonData = {
  id: 'l-05-02',
  title: { fr: "Facture d'achat", ru: 'Счёт покупки' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Analyser une facture fournisseur', ru: 'Анализ счёта поставщика' },
      content: {
        fr: `Lorsqu'on reçoit une **facture d'achat** (fournisseur), on doit:

1. **Vérifier** les mentions obligatoires
2. **Contrôler** les calculs (HT, TVA, TTC)
3. **Enregistrer** dans le journal:
   - Débit: Compte d'achat (6xx) pour le montant HT
   - Débit: TVA déductible (44566) pour la TVA
   - Crédit: Fournisseurs (401) pour le montant TTC

**La TVA déductible** est la TVA que l'entreprise peut récupérer sur ses achats professionnels.

**Formule:** TTC = HT + TVA = HT × (1 + taux)`,
        ru: `При получении **счёта покупки** (поставщика) необходимо:

1. **Проверить** обязательные реквизиты
2. **Контролировать** расчёты (без НДС, НДС, с НДС)
3. **Записать** в журнал:
   - Дебет: Счёт покупки (6xx) на сумму без НДС
   - Дебет: Вычитаемый НДС (44566) на сумму НДС
   - Кредит: Поставщики (401) на сумму с НДС

**Вычитаемый НДС** — это НДС, который предприятие может возместить по профессиональным покупкам.

**Формула:** С НДС = Без НДС + НДС = Без НДС × (1 + ставка)`,
      },
      keyConceptFr: 'La TVA déductible (compte 44566) permet à l\'entreprise de récupérer la TVA payée sur ses achats.',
      keyConceptRu: 'Вычитаемый НДС (счёт 44566) позволяет предприятию возместить НДС, уплаченный по покупкам.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Enregistrement complet avec TVA', ru: 'Пример: Полная запись с НДС' },
      content: {
        fr: `**Facture fournisseur:** Achat de matières premières.
- Montant HT: 2 000 €
- TVA 20%: 400 €
- Total TTC: 2 400 €

| Compte | Libellé | Débit | Crédit |
|--------|---------|-------|--------|
| 601 | Achats matières premières | 2 000 € | |
| 44566 | TVA déductible | 400 € | |
| 401 | Fournisseurs | | 2 400 € |

**Vérification:** 2 000 + 400 = 2 400 ✓`,
        ru: `**Счёт поставщика:** Покупка сырья.
- Сумма без НДС: 2 000 €
- НДС 20%: 400 €
- Итого с НДС: 2 400 €

| Счёт | Описание | Дебет | Кредит |
|------|----------|-------|--------|
| 601 | Покупка сырья | 2 000 € | |
| 44566 | Вычитаемый НДС | 400 € | |
| 401 | Поставщики | | 2 400 € |

**Проверка:** 2 000 + 400 = 2 400 ✓`,
      },
      noteToRememberFr: 'Toujours enregistrer la TVA séparément. Le compte 44566 est un actif (créance sur l\'État).',
      noteToRememberRu: 'Всегда записывайте НДС отдельно. Счёт 44566 — актив (требование к государству).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Calcul TTC', ru: 'Упражнение: Расчёт суммы с НДС' },
      question: {
        fr: 'Facture achat: HT 500 €, TVA 20%. Enregistrez la TVA déductible:',
        ru: 'Счёт покупки: без НДС 500 €, НДС 20%. Запишите вычитаемый НДС:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'TVA déductible (44566) — 100 €', ru: 'Вычитаемый НДС (44566) — 100 €' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Fournisseurs (401) — 600 € TTC', ru: 'Поставщики (401) — 600 € с НДС' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: "Quiz — Facture d'achat", ru: 'Тест — Счёт покупки' },
      questions: [
        {
          id: 'l05-02-q1',
          question: { fr: 'La TVA déductible est enregistrée:', ru: 'Вычитаемый НДС записывается:' },
          options: [
            { id: 'a', text: { fr: 'Au crédit', ru: 'По кредиту' }, isCorrect: false },
            { id: 'b', text: { fr: 'Au débit', ru: 'По дебету' }, isCorrect: true },
            { id: 'c', text: { fr: "Elle n'est pas enregistrée", ru: 'Он не записывается' }, isCorrect: false },
            { id: 'd', text: { fr: 'Dans les charges', ru: 'В расходах' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-02-q2',
          question: { fr: 'HT = 1 000 €, TVA 20%. Quel est le montant TTC?', ru: 'Без НДС = 1 000 €, НДС 20%. Какова сумма с НДС?' },
          options: [
            { id: 'a', text: { fr: '1 100 €', ru: '1 100 €' }, isCorrect: false },
            { id: 'b', text: { fr: '1 200 €', ru: '1 200 €' }, isCorrect: true },
            { id: 'c', text: { fr: '1 020 €', ru: '1 020 €' }, isCorrect: false },
            { id: 'd', text: { fr: '1 500 €', ru: '1 500 €' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-02-q3',
          question: { fr: 'Le compte fournisseur (401) est crédité pour:', ru: 'Счёт поставщика (401) кредитуется на:' },
          options: [
            { id: 'a', text: { fr: 'Le montant HT', ru: 'Сумму без НДС' }, isCorrect: false },
            { id: 'b', text: { fr: 'Le montant de TVA', ru: 'Сумму НДС' }, isCorrect: false },
            { id: 'c', text: { fr: 'Le montant TTC', ru: 'Сумму с НДС' }, isCorrect: true },
            { id: 'd', text: { fr: 'La moitié du montant', ru: 'Половину суммы' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_05_03: LessonData = {
  id: 'l-05-03',
  title: { fr: 'Facture de vente', ru: 'Счёт продажи' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Créer et enregistrer une facture client', ru: 'Создание и запись счёта клиента' },
      content: {
        fr: `Lorsqu'on émet une **facture de vente** (client), on enregistre:

- Débit: Clients (411) pour le montant TTC
- Crédit: Ventes (7xx) pour le montant HT
- Crédit: TVA collectée (44571) pour la TVA

**La TVA collectée** est la TVA que l'entreprise collecte pour le compte de l'État. Elle devra la reverser.

**Différence clé:**
- TVA déductible (achat) → actif → on la récupère
- TVA collectée (vente) → passif → on la reverse à l'État`,
        ru: `При выставлении **счёта продажи** (клиенту) записывается:

- Дебет: Клиенты (411) на сумму с НДС
- Кредит: Продажи (7xx) на сумму без НДС
- Кредит: Собранный НДС (44571) на сумму НДС

**Собранный НДС** — это НДС, который предприятие собирает от имени государства. Его нужно перечислить.

**Ключевое различие:**
- Вычитаемый НДС (покупка) → актив → возмещается
- Собранный НДС (продажа) → пассив → перечисляется государству`,
      },
      keyConceptFr: 'La TVA collectée (compte 44571) est une dette envers l\'État. Elle doit être reversée lors de la déclaration de TVA.',
      keyConceptRu: 'Собранный НДС (счёт 44571) — долг перед государством. Он должен быть перечислен при декларации НДС.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Facture de vente complète', ru: 'Пример: Полный счёт продажи' },
      content: {
        fr: `**Facture client:** Vente de prestations.
- Montant HT: 3 000 €
- TVA 20%: 600 €
- Total TTC: 3 600 €

| Compte | Libellé | Débit | Crédit |
|--------|---------|-------|--------|
| 411 | Clients | 3 600 € | |
| 706 | Prestations de services | | 3 000 € |
| 44571 | TVA collectée | | 600 € |

**Vérification:** 3 600 = 3 000 + 600 ✓`,
        ru: `**Счёт клиента:** Продажа услуг.
- Сумма без НДС: 3 000 €
- НДС 20%: 600 €
- Итого с НДС: 3 600 €

| Счёт | Описание | Дебет | Кредит |
|------|----------|-------|--------|
| 411 | Клиенты | 3 600 € | |
| 706 | Услуги | | 3 000 € |
| 44571 | Собранный НДС | | 600 € |

**Проверка:** 3 600 = 3 000 + 600 ✓`,
      },
      noteToRememberFr: 'Le client paie TTC. L\'entreprise garde HT et reverse la TVA à l\'État.',
      noteToRememberRu: 'Клиент платит с НДС. Предприятие оставляет сумму без НДС и перечисляет НДС государству.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: TVA collectée', ru: 'Упражнение: Собранный НДС' },
      question: {
        fr: 'Vente HT 2 000 €, TVA 20% = 400 €. Enregistrez la TVA collectée:',
        ru: 'Продажа без НДС 2 000 €, НДС 20% = 400 €. Запишите собранный НДС:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Clients (411) — 2 400 € TTC', ru: 'Клиенты (411) — 2 400 € с НДС' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'TVA collectée (44571) — 400 €', ru: 'Собранный НДС (44571) — 400 €' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Facture de vente', ru: 'Тест — Счёт продажи' },
      questions: [
        {
          id: 'l05-03-q1',
          question: { fr: 'La TVA collectée est:', ru: 'Собранный НДС — это:' },
          options: [
            { id: 'a', text: { fr: 'Un actif de l\'entreprise', ru: 'Актив предприятия' }, isCorrect: false },
            { id: 'b', text: { fr: 'Une dette envers l\'État', ru: 'Долг перед государством' }, isCorrect: true },
            { id: 'c', text: { fr: 'Un produit de l\'entreprise', ru: 'Доход предприятия' }, isCorrect: false },
            { id: 'd', text: { fr: 'Une charge de l\'entreprise', ru: 'Расход предприятия' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-03-q2',
          question: { fr: 'Lors d\'une vente, le compte clients (411) est:', ru: 'При продаже счёт клиентов (411):' },
          options: [
            { id: 'a', text: { fr: 'Crédité', ru: 'Кредитуется' }, isCorrect: false },
            { id: 'b', text: { fr: 'Débité', ru: 'Дебетуется' }, isCorrect: true },
            { id: 'c', text: { fr: 'Annulé', ru: 'Аннулируется' }, isCorrect: false },
            { id: 'd', text: { fr: 'Non affecté', ru: 'Не затрагивается' }, isCorrect: false },
          ],
        },
        {
          id: 'l05-03-q3',
          question: { fr: 'Le compte 44571 représente:', ru: 'Счёт 44571 представляет:' },
          options: [
            { id: 'a', text: { fr: 'La TVA déductible', ru: 'Вычитаемый НДС' }, isCorrect: false },
            { id: 'b', text: { fr: 'La TVA collectée', ru: 'Собранный НДС' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les achats', ru: 'Покупки' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 6 LESSONS ─────────────────────────────────────────────────────────

const l_06_01: LessonData = {
  id: 'l-06-01',
  title: { fr: "Qu'est-ce que la TVA?", ru: 'Что такое НДС?' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Principe et taux de TVA', ru: 'Принцип и ставки НДС' },
      content: {
        fr: `La **TVA (Taxe sur la Valeur Ajoutée)** est un impôt indirect sur la consommation.

**Principe:** L'entreprise collecte la TVA pour le compte de l'État.

**Taux en France:**
- **20%** — Taux normal (majorité des biens et services)
- **10%** — Taux intermédiaire (restauration, travaux)
- **5,5%** — Taux réduit (alimentation, livres)
- **2,1%** — Taux super-réduit (médicaments remboursables)

**Mécanisme:**
- TVA collectée (sur ventes) - TVA déductible (sur achats) = TVA à payer`,
        ru: `**НДС (Налог на добавленную стоимость)** — косвенный налог на потребление.

**Принцип:** Предприятие собирает НДС от имени государства.

**Ставки во Франции:**
- **20%** — Стандартная ставка (большинство товаров и услуг)
- **10%** — Промежуточная ставка (рестораны, работы)
- **5,5%** — Сниженная ставка (продукты питания, книги)
- **2,1%** — Сверхсниженная ставка (возмещаемые лекарства)

**Механизм:**
- Собранный НДС (с продаж) - Вычитаемый НДС (с покупок) = НДС к уплате`,
      },
      keyConceptFr: 'L\'entreprise est un collecteur de TVA pour l\'État. Elle ne supporte pas la TVA — c\'est le consommateur final qui la paie.',
      keyConceptRu: 'Предприятие является сборщиком НДС для государства. Оно не несёт НДС — его платит конечный потребитель.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Calcul de TVA', ru: 'Пример: Расчёт НДС' },
      content: {
        fr: `**Scénario mensuel:**

| Opération | HT | TVA 20% | TTC |
|-----------|-----|---------|-----|
| Ventes | 10 000 € | 2 000 € | 12 000 € |
| Achats | 6 000 € | 1 200 € | 7 200 € |

**Calcul TVA à payer:**
TVA collectée: 2 000 €
TVA déductible: - 1 200 €
**TVA nette à payer: 800 €**

L'entreprise verse 800 € à l'État.`,
        ru: `**Ежемесячный сценарий:**

| Операция | Без НДС | НДС 20% | С НДС |
|----------|---------|---------|-------|
| Продажи | 10 000 € | 2 000 € | 12 000 € |
| Покупки | 6 000 € | 1 200 € | 7 200 € |

**Расчёт НДС к уплате:**
Собранный НДС: 2 000 €
Вычитаемый НДС: - 1 200 €
**Чистый НДС к уплате: 800 €**

Предприятие перечисляет 800 € государству.`,
      },
      noteToRememberFr: 'Si TVA déductible > TVA collectée, l\'entreprise a un crédit de TVA remboursable par l\'État.',
      noteToRememberRu: 'Если вычитаемый НДС > собранного НДС, у предприятия есть кредит НДС, возмещаемый государством.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Identifier la TVA', ru: 'Упражнение: Определите НДС' },
      question: {
        fr: "L'entreprise paie la TVA nette de 800 € à l'État:",
        ru: 'Предприятие уплачивает чистый НДС 800 € государству:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'TVA collectée (44571) — diminue', ru: 'Собранный НДС (44571) — уменьшается' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (512) — argent sorti', ru: 'Банк (512) — деньги ушли' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — La TVA', ru: 'Тест — НДС' },
      questions: [
        {
          id: 'l06-01-q1',
          question: { fr: 'Quel est le taux normal de TVA en France?', ru: 'Какова стандартная ставка НДС во Франции?' },
          options: [
            { id: 'a', text: { fr: '10%', ru: '10%' }, isCorrect: false },
            { id: 'b', text: { fr: '20%', ru: '20%' }, isCorrect: true },
            { id: 'c', text: { fr: '5,5%', ru: '5,5%' }, isCorrect: false },
            { id: 'd', text: { fr: '15%', ru: '15%' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-01-q2',
          question: { fr: 'La TVA à payer = TVA collectée:', ru: 'НДС к уплате = Собранный НДС:' },
          options: [
            { id: 'a', text: { fr: '+ TVA déductible', ru: '+ Вычитаемый НДС' }, isCorrect: false },
            { id: 'b', text: { fr: '- TVA déductible', ru: '- Вычитаемый НДС' }, isCorrect: true },
            { id: 'c', text: { fr: '× TVA déductible', ru: '× Вычитаемый НДС' }, isCorrect: false },
            { id: 'd', text: { fr: '÷ TVA déductible', ru: '÷ Вычитаемый НДС' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-01-q3',
          question: { fr: 'Qui supporte réellement la TVA?', ru: 'Кто реально несёт НДС?' },
          options: [
            { id: 'a', text: { fr: "L'entreprise", ru: 'Предприятие' }, isCorrect: false },
            { id: 'b', text: { fr: 'Le consommateur final', ru: 'Конечный потребитель' }, isCorrect: true },
            { id: 'c', text: { fr: "L'État", ru: 'Государство' }, isCorrect: false },
            { id: 'd', text: { fr: 'Le fournisseur', ru: 'Поставщик' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_06_02: LessonData = {
  id: 'l-06-02',
  title: { fr: 'Calcul de TVA', ru: 'Расчёт НДС' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'TVA collectée vs déductible', ru: 'Собранный vs вычитаемый НДС' },
      content: {
        fr: `**Deux types de TVA:**

**TVA collectée (sur ventes):**
- Compte 44571
- C'est un passif (dette envers l'État)
- Elle augmente lors des ventes

**TVA déductible (sur achats):**
- Compte 44566
- C'est un actif (créance sur l'État)
- Elle augmente lors des achats

**Calcul mensuel:**
Si TVA collectée > TVA déductible → **TVA à décaisser** (on paie)
Si TVA déductible > TVA collectée → **Crédit de TVA** (l'État rembourse)`,
        ru: `**Два вида НДС:**

**Собранный НДС (с продаж):**
- Счёт 44571
- Это пассив (долг перед государством)
- Растёт при продажах

**Вычитаемый НДС (с покупок):**
- Счёт 44566
- Это актив (требование к государству)
- Растёт при покупках

**Ежемесячный расчёт:**
Если собранный НДС > вычитаемого → **НДС к уплате** (платим)
Если вычитаемый НДС > собранного → **Кредит НДС** (государство возмещает)`,
      },
      keyConceptFr: 'La TVA est neutre pour l\'entreprise assujettie. Elle collecte et déduit, puis reverse la différence à l\'État.',
      keyConceptRu: 'НДС нейтрален для налогоплательщика. Он собирает и вычитает, затем перечисляет разницу государству.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Calcul détaillé', ru: 'Пример: Детальный расчёт' },
      content: {
        fr: `**Mois de mars:**

Ventes: 15 000 € HT → TVA collectée: 3 000 €
Achats: 8 000 € HT → TVA déductible: 1 600 €

**TVA nette = 3 000 - 1 600 = 1 400 €**

| Compte | Débit | Crédit |
|--------|-------|--------|
| 44571 TVA collectée | 3 000 € | |
| 44566 TVA déductible | | 1 600 € |
| 512 Banque | | 1 400 € |`,
        ru: `**Март месяц:**

Продажи: 15 000 € без НДС → Собранный НДС: 3 000 €
Покупки: 8 000 € без НДС → Вычитаемый НДС: 1 600 €

**Чистый НДС = 3 000 - 1 600 = 1 400 €**

| Счёт | Дебет | Кредит |
|------|-------|--------|
| 44571 Собранный НДС | 3 000 € | |
| 44566 Вычитаемый НДС | | 1 600 € |
| 512 Банк | | 1 400 € |`,
      },
      noteToRememberFr: 'La déclaration de TVA (CA3) est généralement mensuelle pour les grandes entreprises et trimestrielle pour les petites.',
      noteToRememberRu: 'Декларация НДС (CA3) обычно ежемесячная для крупных предприятий и ежеквартальная для малых.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Calculer la TVA nette', ru: 'Упражнение: Рассчитайте чистый НДС' },
      question: {
        fr: 'TVA collectée 2 000 €, TVA déductible 800 €. Enregistrez le paiement TVA:',
        ru: 'Собранный НДС 2 000 €, вычитаемый НДС 800 €. Запишите уплату НДС:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'TVA collectée (44571) — soldée', ru: 'Собранный НДС (44571) — закрыт' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (512) — 1 200 € sortis', ru: 'Банк (512) — 1 200 € ушло' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Calcul TVA', ru: 'Тест — Расчёт НДС' },
      questions: [
        {
          id: 'l06-02-q1',
          question: { fr: 'TVA collectée 5 000 €, TVA déductible 3 000 €. TVA à payer:', ru: 'Собранный НДС 5 000 €, вычитаемый НДС 3 000 €. НДС к уплате:' },
          options: [
            { id: 'a', text: { fr: '8 000 €', ru: '8 000 €' }, isCorrect: false },
            { id: 'b', text: { fr: '2 000 €', ru: '2 000 €' }, isCorrect: true },
            { id: 'c', text: { fr: '3 000 €', ru: '3 000 €' }, isCorrect: false },
            { id: 'd', text: { fr: '5 000 €', ru: '5 000 €' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-02-q2',
          question: { fr: 'Un crédit de TVA signifie:', ru: 'Кредит НДС означает:' },
          options: [
            { id: 'a', text: { fr: "L'entreprise doit payer plus de TVA", ru: 'Предприятие должно заплатить больше НДС' }, isCorrect: false },
            { id: 'b', text: { fr: "L'État doit rembourser l'entreprise", ru: 'Государство должно возместить предприятию' }, isCorrect: true },
            { id: 'c', text: { fr: "La TVA est annulée", ru: 'НДС аннулируется' }, isCorrect: false },
            { id: 'd', text: { fr: "Il n'y a pas de TVA", ru: 'НДС нет' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-02-q3',
          question: { fr: 'Le compte 44566 est:', ru: 'Счёт 44566 — это:' },
          options: [
            { id: 'a', text: { fr: 'Un passif', ru: 'Пассив' }, isCorrect: false },
            { id: 'b', text: { fr: 'Un actif (TVA déductible)', ru: 'Актив (вычитаемый НДС)' }, isCorrect: true },
            { id: 'c', text: { fr: 'Une charge', ru: 'Расход' }, isCorrect: false },
            { id: 'd', text: { fr: 'Un produit', ru: 'Доход' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_06_03: LessonData = {
  id: 'l-06-03',
  title: { fr: 'Déclaration de TVA', ru: 'Декларация НДС' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Remplir la déclaration CA3', ru: 'Заполнение декларации CA3' },
      content: {
        fr: `La **déclaration de TVA (CA3)** est le document officiel envoyé à l'administration fiscale.

**Fréquence:**
- Mensuelle: chiffre d'affaires > 4 000 € de TVA/an
- Trimestrielle: chiffre d'affaires < 4 000 € de TVA/an

**Contenu de la CA3:**
1. Chiffre d'affaires HT par taux
2. TVA collectée totale
3. TVA déductible sur immobilisations
4. TVA déductible sur autres biens et services
5. **TVA nette à payer** (ou crédit)

**Délai:** La CA3 doit être déposée et payée avant le 19 du mois suivant.`,
        ru: `**Декларация НДС (CA3)** — официальный документ, направляемый в налоговую администрацию.

**Периодичность:**
- Ежемесячная: оборот > 4 000 € НДС/год
- Ежеквартальная: оборот < 4 000 € НДС/год

**Содержание CA3:**
1. Оборот без НДС по ставкам
2. Общий собранный НДС
3. Вычитаемый НДС по основным средствам
4. Вычитаемый НДС по прочим товарам и услугам
5. **Чистый НДС к уплате** (или кредит)

**Срок:** CA3 должна быть подана и оплачена до 19-го числа следующего месяца.`,
      },
      keyConceptFr: 'La CA3 est la déclaration mensuelle ou trimestrielle de TVA. Elle récapitule toutes les opérations soumises à TVA.',
      keyConceptRu: 'CA3 — ежемесячная или ежеквартальная декларация НДС. Она обобщает все операции, облагаемые НДС.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: CA3 simplifiée', ru: 'Пример: Упрощённая CA3' },
      content: {
        fr: `**Déclaration CA3 — Mars 2026:**

| Ligne | Description | Montant |
|-------|-------------|---------|
| CA | Chiffre d'affaires HT | 20 000 € |
| TVA collectée 20% | 20 000 × 20% | 4 000 € |
| TVA déductible achats | Factures fournisseurs | -2 500 € |
| **TVA nette** | **À payer** | **1 500 €** |

Paiement de 1 500 € avant le 19/04/2026.`,
        ru: `**Декларация CA3 — Март 2026:**

| Строка | Описание | Сумма |
|--------|----------|-------|
| CA | Оборот без НДС | 20 000 € |
| Собранный НДС 20% | 20 000 × 20% | 4 000 € |
| Вычитаемый НДС покупки | Счета поставщиков | -2 500 € |
| **Чистый НДС** | **К уплате** | **1 500 €** |

Оплата 1 500 € до 19/04/2026.`,
      },
      noteToRememberFr: 'En cas de retard de paiement de TVA, des pénalités de 10% s\'appliquent, plus des intérêts de retard.',
      noteToRememberRu: 'В случае просрочки уплаты НДС применяются штрафы 10%, плюс проценты за просрочку.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Clôture TVA', ru: 'Упражнение: Закрытие НДС' },
      question: {
        fr: 'Paiement TVA nette 1 500 € à l\'État. Enregistrez:',
        ru: 'Уплата чистого НДС 1 500 € государству. Запишите:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'TVA à décaisser (44551)', ru: 'НДС к уплате (44551)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (512)', ru: 'Банк (512)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Déclaration TVA', ru: 'Тест — Декларация НДС' },
      questions: [
        {
          id: 'l06-03-q1',
          question: { fr: 'La CA3 doit être déposée avant le:', ru: 'CA3 должна быть подана до:' },
          options: [
            { id: 'a', text: { fr: '1er du mois suivant', ru: '1-го числа следующего месяца' }, isCorrect: false },
            { id: 'b', text: { fr: '19 du mois suivant', ru: '19-го числа следующего месяца' }, isCorrect: true },
            { id: 'c', text: { fr: '31 du même mois', ru: '31-го числа того же месяца' }, isCorrect: false },
            { id: 'd', text: { fr: '15 du mois suivant', ru: '15-го числа следующего месяца' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-03-q2',
          question: { fr: 'La déclaration de TVA mensuelle s\'appelle:', ru: 'Ежемесячная декларация НДС называется:' },
          options: [
            { id: 'a', text: { fr: 'CA12', ru: 'CA12' }, isCorrect: false },
            { id: 'b', text: { fr: 'CA3', ru: 'CA3' }, isCorrect: true },
            { id: 'c', text: { fr: 'TVA2026', ru: 'TVA2026' }, isCorrect: false },
            { id: 'd', text: { fr: 'DAS2', ru: 'DAS2' }, isCorrect: false },
          ],
        },
        {
          id: 'l06-03-q3',
          question: { fr: 'La pénalité de retard de TVA est de:', ru: 'Штраф за просрочку НДС составляет:' },
          options: [
            { id: 'a', text: { fr: '5%', ru: '5%' }, isCorrect: false },
            { id: 'b', text: { fr: '10%', ru: '10%' }, isCorrect: true },
            { id: 'c', text: { fr: '20%', ru: '20%' }, isCorrect: false },
            { id: 'd', text: { fr: '2%', ru: '2%' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 7 LESSONS ─────────────────────────────────────────────────────────

const l_07_01: LessonData = {
  id: 'l-07-01',
  title: { fr: 'Structure du grand livre', ru: 'Структура главной книги' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Organisation des comptes', ru: 'Организация счетов' },
      content: {
        fr: `Le **grand livre** est le registre central de la comptabilité. Il regroupe tous les comptes de l'entreprise.

**Structure:**
- Un compte par ligne du Plan Comptable Général
- Chaque compte montre tous les mouvements débit/crédit
- Le solde est calculé en permanence

**Utilité:**
- Retrouver tous les mouvements d'un compte
- Vérifier les soldes
- Préparer la balance de vérification
- Établir le bilan et le compte de résultat

**Exemple de compte en T:**
Compte 512 — Banque
| Débit | Crédit |
|-------|--------|
| Encaissements | Décaissements |`,
        ru: `**Главная книга** — центральный реестр бухгалтерии. В ней собраны все счета предприятия.

**Структура:**
- Один счёт на строку Общего плана счетов
- Каждый счёт показывает все движения дебет/кредит
- Остаток рассчитывается постоянно

**Назначение:**
- Найти все движения по счёту
- Проверить остатки
- Подготовить проверочный баланс
- Составить баланс и отчёт о прибылях

**Пример Т-счёта:**
Счёт 512 — Банк
| Дебет | Кредит |
|-------|--------|
| Поступления | Выплаты |`,
      },
      keyConceptFr: 'Le grand livre est le "livre maître" de la comptabilité. Chaque compte y est détaillé avec tous ses mouvements.',
      keyConceptRu: 'Главная книга — "главная книга" бухгалтерии. Каждый счёт детализирован со всеми движениями.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Compte banque du mois', ru: 'Пример: Счёт банка за месяц' },
      content: {
        fr: `**Compte 512 — Banque — Mars 2026:**

| Date | Libellé | Débit | Crédit | Solde |
|------|---------|-------|--------|-------|
| 01/03 | Solde initial | | | 5 000 € |
| 05/03 | Vente client | 3 000 € | | 8 000 € |
| 10/03 | Achat fournisseur | | 1 500 € | 6 500 € |
| 20/03 | Loyer | | 800 € | 5 700 € |
| 31/03 | **Solde final** | | | **5 700 €** |`,
        ru: `**Счёт 512 — Банк — Март 2026:**

| Дата | Описание | Дебет | Кредит | Остаток |
|------|----------|-------|--------|---------|
| 01/03 | Начальный остаток | | | 5 000 € |
| 05/03 | Продажа клиенту | 3 000 € | | 8 000 € |
| 10/03 | Покупка у поставщика | | 1 500 € | 6 500 € |
| 20/03 | Аренда | | 800 € | 5 700 € |
| 31/03 | **Конечный остаток** | | | **5 700 €** |`,
      },
      noteToRememberFr: 'Le solde d\'un compte = Total débits - Total crédits (pour les comptes d\'actif et charges).',
      noteToRememberRu: 'Остаток счёта = Итого дебетов - Итого кредитов (для счетов актива и расходов).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Solde de compte', ru: 'Упражнение: Остаток счёта' },
      question: {
        fr: 'Virement reçu de 2 000 € sur le compte banque. Enregistrez:',
        ru: 'Получен перевод 2 000 € на банковский счёт. Запишите:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (512) — augmente', ru: 'Банк (512) — растёт' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Clients (411) — diminue', ru: 'Клиенты (411) — уменьшается' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Grand livre', ru: 'Тест — Главная книга' },
      questions: [
        {
          id: 'l07-01-q1',
          question: { fr: 'Le grand livre regroupe:', ru: 'Главная книга объединяет:' },
          options: [
            { id: 'a', text: { fr: 'Seulement les comptes de banque', ru: 'Только банковские счета' }, isCorrect: false },
            { id: 'b', text: { fr: 'Tous les comptes de l\'entreprise', ru: 'Все счета предприятия' }, isCorrect: true },
            { id: 'c', text: { fr: 'Seulement les charges', ru: 'Только расходы' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les factures uniquement', ru: 'Только счета-фактуры' }, isCorrect: false },
          ],
        },
        {
          id: 'l07-01-q2',
          question: { fr: 'Le solde d\'un compte actif est normalement:', ru: 'Остаток счёта актива обычно:' },
          options: [
            { id: 'a', text: { fr: 'Créditeur', ru: 'Кредитовый' }, isCorrect: false },
            { id: 'b', text: { fr: 'Débiteur', ru: 'Дебетовый' }, isCorrect: true },
            { id: 'c', text: { fr: 'Nul', ru: 'Нулевой' }, isCorrect: false },
            { id: 'd', text: { fr: 'Négatif', ru: 'Отрицательный' }, isCorrect: false },
          ],
        },
        {
          id: 'l07-01-q3',
          question: { fr: 'À quoi sert le grand livre?', ru: 'Для чего нужна главная книга?' },
          options: [
            { id: 'a', text: { fr: 'À payer les salaires', ru: 'Для выплаты зарплат' }, isCorrect: false },
            { id: 'b', text: { fr: 'À retrouver tous les mouvements d\'un compte', ru: 'Для поиска всех движений по счёту' }, isCorrect: true },
            { id: 'c', text: { fr: 'À recruter des employés', ru: 'Для найма сотрудников' }, isCorrect: false },
            { id: 'd', text: { fr: 'À calculer les impôts', ru: 'Для расчёта налогов' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_07_02: LessonData = {
  id: 'l-07-02',
  title: { fr: 'Balance de vérification', ru: 'Проверочный баланс' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Contrôler les soldes', ru: 'Контроль остатков' },
      content: {
        fr: `La **balance de vérification** est un tableau récapitulatif de tous les comptes du grand livre.

**Structure:**
- Numéro de compte
- Intitulé du compte
- Total des débits
- Total des crédits
- Solde débiteur ou créditeur

**Règle fondamentale:**
Total des débits = Total des crédits (pour tous les comptes)

**Utilité:**
- Détecter les erreurs d'enregistrement
- Préparer le bilan et le compte de résultat
- Vérifier l'équilibre comptable Si la balance ne s'équilibre pas → il y a une erreur à corriger!`,
        ru: `**Проверочный баланс** — сводная таблица всех счетов главной книги.

**Структура:**
- Номер счёта
- Наименование счёта
- Итого дебетов
- Итого кредитов
- Дебетовый или кредитовый остаток

**Фундаментальное правило:**
Итого дебетов = Итого кредитов (по всем счетам)

**Назначение:**
- Выявление ошибок записи
- Подготовка баланса и отчёта о прибылях
- Проверка бухгалтерского равновесия

Если баланс не сходится → есть ошибка для исправления!`,
      },
      keyConceptFr: 'La balance de vérification prouve que les débits = crédits. Si elle ne s\'équilibre pas, il y a une erreur.',
      keyConceptRu: 'Проверочный баланс доказывает, что дебеты = кредиты. Если он не сходится, есть ошибка.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Balance simplifiée', ru: 'Пример: Упрощённый проверочный баланс' },
      content: {
        fr: `**Balance de vérification — 31/03/2026:**

| Compte | Intitulé | Débit | Crédit |
|--------|----------|-------|--------|
| 101 | Capital | | 50 000 € |
| 512 | Banque | 35 000 € | |
| 411 | Clients | 8 000 € | |
| 401 | Fournisseurs | | 5 000 € |
| 607 | Achats | 12 000 € | |
| **Total** | | **55 000 €** | **55 000 €** |

✓ Balance équilibrée: 55 000 = 55 000`,
        ru: `**Проверочный баланс — 31/03/2026:**

| Счёт | Наименование | Дебет | Кредит |
|------|--------------|-------|--------|
| 101 | Капитал | | 50 000 € |
| 512 | Банк | 35 000 € | |
| 411 | Клиенты | 8 000 € | |
| 401 | Поставщики | | 5 000 € |
| 607 | Покупки | 12 000 € | |
| **Итого** | | **55 000 €** | **55 000 €** |

✓ Баланс сходится: 55 000 = 55 000`,
      },
      noteToRememberFr: 'La balance de vérification est établie avant le bilan. Elle sert de base pour les états financiers.',
      noteToRememberRu: 'Проверочный баланс составляется до баланса. Он служит основой для финансовой отчётности.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Vérifier la balance', ru: 'Упражнение: Проверьте баланс' },
      question: {
        fr: 'Paiement salaires 3 000 € par banque. Enregistrez:',
        ru: 'Выплата зарплат 3 000 € через банк. Запишите:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Salaires (641) — charge', ru: 'Зарплаты (641) — расход' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (512) — diminue', ru: 'Банк (512) — уменьшается' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Balance de vérification', ru: 'Тест — Проверочный баланс' },
      questions: [
        {
          id: 'l07-02-q1',
          question: { fr: 'Dans une balance équilibrée:', ru: 'В сбалансированном проверочном балансе:' },
          options: [
            { id: 'a', text: { fr: 'Débits > Crédits', ru: 'Дебеты > Кредиты' }, isCorrect: false },
            { id: 'b', text: { fr: 'Débits = Crédits', ru: 'Дебеты = Кредиты' }, isCorrect: true },
            { id: 'c', text: { fr: 'Crédits > Débits', ru: 'Кредиты > Дебеты' }, isCorrect: false },
            { id: 'd', text: { fr: 'Débits = 0', ru: 'Дебеты = 0' }, isCorrect: false },
          ],
        },
        {
          id: 'l07-02-q2',
          question: { fr: 'Si la balance ne s\'équilibre pas, cela signifie:', ru: 'Если баланс не сходится, это означает:' },
          options: [
            { id: 'a', text: { fr: "L'entreprise est en faillite", ru: 'Предприятие банкрот' }, isCorrect: false },
            { id: 'b', text: { fr: "Il y a une erreur d'enregistrement", ru: 'Есть ошибка записи' }, isCorrect: true },
            { id: 'c', text: { fr: "Les ventes sont nulles", ru: 'Продажи нулевые' }, isCorrect: false },
            { id: 'd', text: { fr: "La TVA n'est pas payée", ru: 'НДС не уплачен' }, isCorrect: false },
          ],
        },
        {
          id: 'l07-02-q3',
          question: { fr: 'La balance de vérification est établie:', ru: 'Проверочный баланс составляется:' },
          options: [
            { id: 'a', text: { fr: 'Après le bilan', ru: 'После баланса' }, isCorrect: false },
            { id: 'b', text: { fr: 'Avant le bilan', ru: 'До баланса' }, isCorrect: true },
            { id: 'c', text: { fr: 'En même temps que le bilan', ru: 'Одновременно с балансом' }, isCorrect: false },
            { id: 'd', text: { fr: "Elle n'est pas liée au bilan", ru: 'Она не связана с балансом' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 8 LESSONS ─────────────────────────────────────────────────────────

const l_08_01: LessonData = {
  id: 'l-08-01',
  title: { fr: 'Structure du bilan', ru: 'Структура баланса' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Actif et passif du bilan', ru: 'Актив и пассив баланса' },
      content: {
        fr: `Le **bilan** est l'état financier qui présente la situation patrimoniale de l'entreprise à une date donnée.

**ACTIF (ce que l'entreprise possède):**
- Actif immobilisé: biens durables (machines, bâtiments, brevets)
- Actif circulant: biens à court terme (stocks, créances, trésorerie)

**PASSIF (comment c'est financé):**
- Capitaux propres: capital, réserves, résultat
- Dettes à long terme: emprunts bancaires
- Dettes à court terme: fournisseurs, TVA, salaires

**Règle:** ACTIF TOTAL = PASSIF TOTAL (toujours équilibré)`,
        ru: `**Баланс** — финансовый отчёт, представляющий имущественное положение предприятия на определённую дату.

**АКТИВ (то, чем владеет предприятие):**
- Внеоборотные активы: долгосрочное имущество (машины, здания, патенты)
- Оборотные активы: краткосрочное имущество (запасы, дебиторская задолженность, денежные средства)

**ПАССИВ (как это финансируется):**
- Собственный капитал: капитал, резервы, результат
- Долгосрочные долги: банковские кредиты
- Краткосрочные долги: поставщики, НДС, зарплаты

**Правило:** ИТОГО АКТИВ = ИТОГО ПАССИВ (всегда сбалансировано)`,
      },
      keyConceptFr: 'Le bilan est une "photo" de l\'entreprise à un instant T. Il montre ce qu\'elle possède et comment c\'est financé.',
      keyConceptRu: 'Баланс — это "фотография" предприятия в момент T. Он показывает, чем оно владеет и как это финансируется.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Bilan simplifié', ru: 'Пример: Упрощённый баланс' },
      content: {
        fr: `**BILAN au 31/12/2026:**

| ACTIF | € | PASSIF | € |
|-------|---|--------|---|
| Machines | 40 000 | Capital | 50 000 |
| Stocks | 15 000 | Résultat | 10 000 |
| Clients | 12 000 | Emprunt | 20 000 |
| Banque | 18 000 | Fournisseurs | 5 000 |
| **Total** | **85 000** | **Total** | **85 000** |

✓ Actif = Passif: 85 000 = 85 000`,
        ru: `**БАЛАНС на 31/12/2026:**

| АКТИВ | € | ПАССИВ | € |
|-------|---|--------|---|
| Машины | 40 000 | Капитал | 50 000 |
| Запасы | 15 000 | Результат | 10 000 |
| Клиенты | 12 000 | Кредит | 20 000 |
| Банк | 18 000 | Поставщики | 5 000 |
| **Итого** | **85 000** | **Итого** | **85 000** |

✓ Актив = Пассив: 85 000 = 85 000`,
      },
      noteToRememberFr: 'L\'actif est classé par ordre de liquidité croissante (du moins liquide au plus liquide).',
      noteToRememberRu: 'Актив классифицируется по возрастающей ликвидности (от наименее ликвидного к наиболее ликвидному).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Classer dans le bilan', ru: 'Упражнение: Классифицируйте в балансе' },
      question: {
        fr: "Achat d'un bâtiment 100 000 € financé par emprunt. Enregistrez:",
        ru: 'Покупка здания 100 000 €, финансированная кредитом. Запишите:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Bâtiments (211) — actif immobilisé', ru: 'Здания (211) — внеоборотный актив' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Emprunt bancaire (164) — passif', ru: 'Банковский кредит (164) — пассив' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Structure du bilan', ru: 'Тест — Структура баланса' },
      questions: [
        {
          id: 'l08-01-q1',
          question: { fr: 'Le bilan présente la situation de l\'entreprise:', ru: 'Баланс представляет положение предприятия:' },
          options: [
            { id: 'a', text: { fr: 'Sur une période', ru: 'За период' }, isCorrect: false },
            { id: 'b', text: { fr: 'À une date donnée', ru: 'На определённую дату' }, isCorrect: true },
            { id: 'c', text: { fr: 'Sur 5 ans', ru: 'За 5 лет' }, isCorrect: false },
            { id: 'd', text: { fr: 'Chaque semaine', ru: 'Каждую неделю' }, isCorrect: false },
          ],
        },
        {
          id: 'l08-01-q2',
          question: { fr: 'Les capitaux propres font partie:', ru: 'Собственный капитал является частью:' },
          options: [
            { id: 'a', text: { fr: "De l'actif", ru: 'Актива' }, isCorrect: false },
            { id: 'b', text: { fr: 'Du passif', ru: 'Пассива' }, isCorrect: true },
            { id: 'c', text: { fr: 'Des charges', ru: 'Расходов' }, isCorrect: false },
            { id: 'd', text: { fr: 'Des produits', ru: 'Доходов' }, isCorrect: false },
          ],
        },
        {
          id: 'l08-01-q3',
          question: { fr: 'L\'actif circulant comprend:', ru: 'Оборотные активы включают:' },
          options: [
            { id: 'a', text: { fr: 'Les machines et bâtiments', ru: 'Машины и здания' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les stocks, créances et trésorerie', ru: 'Запасы, дебиторскую задолженность и денежные средства' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les emprunts bancaires', ru: 'Банковские кредиты' }, isCorrect: false },
            { id: 'd', text: { fr: 'Le capital social', ru: 'Уставный капитал' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_08_02: LessonData = {
  id: 'l-08-02',
  title: { fr: 'Lire un bilan', ru: 'Чтение баланса' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Interpréter les données du bilan', ru: 'Интерпретация данных баланса' },
      content: {
        fr: `Savoir **lire un bilan** permet d'évaluer la santé financière d'une entreprise.

**Indicateurs clés:**

**Fonds de roulement (FR):**
FR = Capitaux permanents - Actif immobilisé
Si FR > 0 → l'entreprise finance ses actifs à long terme correctement

**Besoin en fonds de roulement (BFR):**
BFR = Actif circulant (hors trésorerie) - Dettes à court terme (hors banque)
Si BFR > 0 → l'entreprise a besoin de financement à court terme

**Trésorerie nette:**
Trésorerie = FR - BFR
Si Trésorerie > 0 → bonne santé financière`,
        ru: `Умение **читать баланс** позволяет оценить финансовое здоровье предприятия.

**Ключевые показатели:**

**Рабочий капитал (FR):**
FR = Постоянный капитал - Внеоборотные активы
Если FR > 0 → предприятие правильно финансирует долгосрочные активы

**Потребность в рабочем капитале (BFR):**
BFR = Оборотные активы (без денежных средств) - Краткосрочные долги (без банка)
Если BFR > 0 → предприятию нужно краткосрочное финансирование

**Чистые денежные средства:**
Денежные средства = FR - BFR
Если Денежные средства > 0 → хорошее финансовое здоровье`,
      },
      keyConceptFr: 'Un bilan sain montre un fonds de roulement positif et une trésorerie positive. C\'est le signe d\'une entreprise solide.',
      keyConceptRu: 'Здоровый баланс показывает положительный рабочий капитал и положительные денежные средства. Это признак устойчивого предприятия.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Analyse du bilan', ru: 'Пример: Анализ баланса' },
      content: {
        fr: `**Bilan simplifié:**
- Actif immobilisé: 60 000 €
- Actif circulant: 40 000 € (stocks 20 000 + clients 15 000 + banque 5 000)
- Capitaux propres: 50 000 €
- Dettes LT: 20 000 €
- Dettes CT: 30 000 €

**Calculs:**
FR = (50 000 + 20 000) - 60 000 = **10 000 €** ✓ Positif
BFR = (20 000 + 15 000) - 30 000 = **5 000 €**
Trésorerie = 10 000 - 5 000 = **5 000 €** ✓ Saine`,
        ru: `**Упрощённый баланс:**
- Внеоборотные активы: 60 000 €
- Оборотные активы: 40 000 € (запасы 20 000 + клиенты 15 000 + банк 5 000)
- Собственный капитал: 50 000 €
- Долгосрочные долги: 20 000 €
- Краткосрочные долги: 30 000 €

**Расчёты:**
FR = (50 000 + 20 000) - 60 000 = **10 000 €** ✓ Положительный
BFR = (20 000 + 15 000) - 30 000 = **5 000 €**
Денежные средства = 10 000 - 5 000 = **5 000 €** ✓ Здоровые`,
      },
      noteToRememberFr: 'Un fonds de roulement négatif signifie que l\'entreprise finance ses actifs à long terme avec des dettes à court terme — risqué!',
      noteToRememberRu: 'Отрицательный рабочий капитал означает, что предприятие финансирует долгосрочные активы краткосрочными долгами — рискованно!',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Fonds de roulement', ru: 'Упражнение: Рабочий капитал' },
      question: {
        fr: 'Augmentation du capital de 10 000 €. Enregistrez:',
        ru: 'Увеличение капитала на 10 000 €. Запишите:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (512) — actif augmente', ru: 'Банк (512) — актив растёт' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Capital (101) — capitaux propres', ru: 'Капитал (101) — собственный капитал' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Lire un bilan', ru: 'Тест — Чтение баланса' },
      questions: [
        {
          id: 'l08-02-q1',
          question: { fr: 'Un fonds de roulement positif signifie:', ru: 'Положительный рабочий капитал означает:' },
          options: [
            { id: 'a', text: { fr: "L'entreprise est en difficulté", ru: 'Предприятие в затруднении' }, isCorrect: false },
            { id: 'b', text: { fr: "L'entreprise finance correctement ses actifs LT", ru: 'Предприятие правильно финансирует долгосрочные активы' }, isCorrect: true },
            { id: 'c', text: { fr: "L'entreprise a trop de dettes", ru: 'Предприятие имеет слишком много долгов' }, isCorrect: false },
            { id: 'd', text: { fr: "L'entreprise n'a pas de clients", ru: 'У предприятия нет клиентов' }, isCorrect: false },
          ],
        },
        {
          id: 'l08-02-q2',
          question: { fr: 'La trésorerie nette = FR:', ru: 'Чистые денежные средства = FR:' },
          options: [
            { id: 'a', text: { fr: '+ BFR', ru: '+ BFR' }, isCorrect: false },
            { id: 'b', text: { fr: '- BFR', ru: '- BFR' }, isCorrect: true },
            { id: 'c', text: { fr: '× BFR', ru: '× BFR' }, isCorrect: false },
            { id: 'd', text: { fr: '÷ BFR', ru: '÷ BFR' }, isCorrect: false },
          ],
        },
        {
          id: 'l08-02-q3',
          question: { fr: 'Les capitaux permanents comprennent:', ru: 'Постоянный капитал включает:' },
          options: [
            { id: 'a', text: { fr: 'Seulement le capital social', ru: 'Только уставный капитал' }, isCorrect: false },
            { id: 'b', text: { fr: 'Capitaux propres + dettes à long terme', ru: 'Собственный капитал + долгосрочные долги' }, isCorrect: true },
            { id: 'c', text: { fr: 'Seulement les emprunts', ru: 'Только кредиты' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les dettes à court terme', ru: 'Краткосрочные долги' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 9 LESSONS ─────────────────────────────────────────────────────────

const l_09_01: LessonData = {
  id: 'l-09-01',
  title: { fr: 'Structure du compte de résultat', ru: 'Структура отчёта о прибылях' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Produits et charges', ru: 'Доходы и расходы' },
      content: {
        fr: `Le **compte de résultat** mesure la performance de l'entreprise sur une période.

**PRODUITS (revenus):**
- Chiffre d'affaires (ventes)
- Produits financiers (intérêts reçus)
- Produits exceptionnels

**CHARGES (dépenses):**
- Achats de marchandises
- Salaires et charges sociales
- Loyers, assurances
- Amortissements
- Charges financières (intérêts payés)

**RÉSULTAT:**
Résultat = Total Produits - Total Charges
- Si positif → **Bénéfice**
- Si négatif → **Perte**`,
        ru: `**Отчёт о прибылях** измеряет эффективность предприятия за период.

**ДОХОДЫ:**
- Оборот (продажи)
- Финансовые доходы (полученные проценты)
- Исключительные доходы

**РАСХОДЫ:**
- Покупка товаров
- Зарплаты и социальные отчисления
- Аренда, страхование
- Амортизация
- Финансовые расходы (уплаченные проценты)

**РЕЗУЛЬТАТ:**
Результат = Итого Доходы - Итого Расходы
- Если положительный → **Прибыль**
- Если отрицательный → **Убыток**`,
      },
      keyConceptFr: 'Le compte de résultat montre si l\'entreprise a gagné ou perdu de l\'argent sur la période. C\'est le "film" de l\'activité.',
      keyConceptRu: 'Отчёт о прибылях показывает, заработало ли предприятие или потеряло деньги за период. Это "фильм" деятельности.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Compte de résultat simplifié', ru: 'Пример: Упрощённый отчёт о прибылях' },
      content: {
        fr: `**Compte de résultat — Exercice 2026:**

| PRODUITS | € | CHARGES | € |
|----------|---|---------|---|
| Ventes | 80 000 | Achats | 35 000 |
| Prod. financiers | 500 | Salaires | 20 000 |
| | | Loyer | 6 000 |
| | | Amortissements | 4 000 |
| | | Charges financières | 1 500 |
| **Total produits** | **80 500** | **Total charges** | **66 500** |

**Résultat net = 80 500 - 66 500 = 14 000 € (Bénéfice)**`,
        ru: `**Отчёт о прибылях — Год 2026:**

| ДОХОДЫ | € | РАСХОДЫ | € |
|--------|---|---------|---|
| Продажи | 80 000 | Покупки | 35 000 |
| Фин. доходы | 500 | Зарплаты | 20 000 |
| | | Аренда | 6 000 |
| | | Амортизация | 4 000 |
| | | Фин. расходы | 1 500 |
| **Итого доходы** | **80 500** | **Итого расходы** | **66 500** |

**Чистая прибыль = 80 500 - 66 500 = 14 000 € (Прибыль)**`,
      },
      noteToRememberFr: 'Le résultat du compte de résultat est reporté dans les capitaux propres du bilan.',
      noteToRememberRu: 'Результат отчёта о прибылях переносится в собственный капитал баланса.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Classer produits et charges', ru: 'Упражнение: Классифицируйте доходы и расходы' },
      question: {
        fr: 'Enregistrement des ventes du mois 10 000 €:',
        ru: 'Запись продаж за месяц 10 000 €:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (512) — argent reçu', ru: 'Банк (512) — деньги получены' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Ventes (707) — produit', ru: 'Продажи (707) — доход' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Compte de résultat', ru: 'Тест — Отчёт о прибылях' },
      questions: [
        {
          id: 'l09-01-q1',
          question: { fr: 'Le compte de résultat mesure:', ru: 'Отчёт о прибылях измеряет:' },
          options: [
            { id: 'a', text: { fr: 'La situation à une date donnée', ru: 'Положение на определённую дату' }, isCorrect: false },
            { id: 'b', text: { fr: 'La performance sur une période', ru: 'Эффективность за период' }, isCorrect: true },
            { id: 'c', text: { fr: 'Les dettes uniquement', ru: 'Только долги' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les actifs uniquement', ru: 'Только активы' }, isCorrect: false },
          ],
        },
        {
          id: 'l09-01-q2',
          question: { fr: 'Si Produits > Charges, l\'entreprise réalise:', ru: 'Если Доходы > Расходы, предприятие получает:' },
          options: [
            { id: 'a', text: { fr: 'Une perte', ru: 'Убыток' }, isCorrect: false },
            { id: 'b', text: { fr: 'Un bénéfice', ru: 'Прибыль' }, isCorrect: true },
            { id: 'c', text: { fr: 'Un résultat nul', ru: 'Нулевой результат' }, isCorrect: false },
            { id: 'd', text: { fr: 'Une dette', ru: 'Долг' }, isCorrect: false },
          ],
        },
        {
          id: 'l09-01-q3',
          question: { fr: 'Les amortissements sont:', ru: 'Амортизация — это:' },
          options: [
            { id: 'a', text: { fr: 'Des produits', ru: 'Доходы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Des charges', ru: 'Расходы' }, isCorrect: true },
            { id: 'c', text: { fr: 'Des actifs', ru: 'Активы' }, isCorrect: false },
            { id: 'd', text: { fr: 'Des dettes', ru: 'Долги' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_09_02: LessonData = {
  id: 'l-09-02',
  title: { fr: 'Calculer le résultat net', ru: 'Расчёт чистой прибыли' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Profit ou perte?', ru: 'Прибыль или убыток?' },
      content: {
        fr: `Le **résultat net** est le bénéfice ou la perte finale après tous les impôts.

**Calcul en cascade:**
1. **Résultat d'exploitation** = Produits d'exploitation - Charges d'exploitation
2. **Résultat financier** = Produits financiers - Charges financières
3. **Résultat courant** = Résultat d'exploitation + Résultat financier
4. **Résultat exceptionnel** = Produits exceptionnels - Charges exceptionnelles
5. **Résultat avant impôt** = Résultat courant + Résultat exceptionnel
6. **Impôt sur les sociétés** (IS) = 25% du résultat avant impôt (si positif)
7. **Résultat net** = Résultat avant impôt - IS`,
        ru: `**Чистая прибыль** — окончательная прибыль или убыток после всех налогов.

**Каскадный расчёт:**
1. **Операционный результат** = Операционные доходы - Операционные расходы
2. **Финансовый результат** = Финансовые доходы - Финансовые расходы
3. **Текущий результат** = Операционный результат + Финансовый результат
4. **Исключительный результат** = Исключительные доходы - Исключительные расходы
5. **Результат до налогов** = Текущий результат + Исключительный результат
6. **Налог на прибыль** (IS) = 25% от результата до налогов (если положительный)
7. **Чистая прибыль** = Результат до налогов - IS`,
      },
      keyConceptFr: 'Le résultat net est le "bottom line" — ce qui reste après toutes les charges et impôts. C\'est la vraie performance.',
      keyConceptRu: 'Чистая прибыль — это "итоговая строка" — то, что остаётся после всех расходов и налогов. Это реальная эффективность.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Calcul complet', ru: 'Пример: Полный расчёт' },
      content: {
        fr: `**Calcul du résultat net:**

Produits d'exploitation: 100 000 € Charges d'exploitation: - 75 000 €
**Résultat d'exploitation: 25 000 €**

Produits financiers: 500 €
Charges financières: - 2 000 €
**Résultat financier: -1 500 €**

**Résultat courant: 23 500 €**
Résultat exceptionnel: 0 €
**Résultat avant IS: 23 500 €**
IS (25%): - 5 875 €
**Résultat net: 17 625 €**`,
        ru: `**Расчёт чистой прибыли:**

Операционные доходы: 100 000 €
Операционные расходы: - 75 000 €
**Операционный результат: 25 000 €**

Финансовые доходы: 500 €
Финансовые расходы: - 2 000 €
**Финансовый результат: -1 500 €**

**Текущий результат: 23 500 €**
Исключительный результат: 0 €
**Результат до налогов: 23 500 €**
Налог на прибыль (25%): - 5 875 €
**Чистая прибыль: 17 625 €**`,
      },
      noteToRememberFr: 'Le taux d\'IS en France est de 25% depuis 2022 pour toutes les entreprises (sauf PME éligibles au taux réduit de 15%).',
      noteToRememberRu: 'Ставка налога на прибыль во Франции составляет 25% с 2022 года для всех предприятий (кроме МСП, имеющих право на сниженную ставку 15%).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Impôt sur les sociétés', ru: 'Упражнение: Налог на прибыль' },
      question: {
        fr: 'Enregistrement IS de 5 000 €:',
        ru: 'Запись налога на прибыль 5 000 €:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Impôt sur les bénéfices (695) — charge', ru: 'Налог на прибыль (695) — расход' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'État — IS à payer (444) — passif', ru: 'Государство — налог к уплате (444) — пассив' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Résultat net', ru: 'Тест — Чистая прибыль' },
      questions: [
        {
          id: 'l09-02-q1',
          question: { fr: 'Le taux d\'IS en France est de:', ru: 'Ставка налога на прибыль во Франции:' },
          options: [
            { id: 'a', text: { fr: '20%', ru: '20%' }, isCorrect: false },
            { id: 'b', text: { fr: '25%', ru: '25%' }, isCorrect: true },
            { id: 'c', text: { fr: '33%', ru: '33%' }, isCorrect: false },
            { id: 'd', text: { fr: '15%', ru: '15%' }, isCorrect: false },
          ],
        },
        {
          id: 'l09-02-q2',
          question: { fr: 'Le résultat net = Résultat avant IS:', ru: 'Чистая прибыль = Результат до налогов:' },
          options: [
            { id: 'a', text: { fr: '+ IS', ru: '+ IS' }, isCorrect: false },
            { id: 'b', text: { fr: '- IS', ru: '- IS' }, isCorrect: true },
            { id: 'c', text: { fr: '× IS', ru: '× IS' }, isCorrect: false },
            { id: 'd', text: { fr: '÷ IS', ru: '÷ IS' }, isCorrect: false },
          ],
        },
        {
          id: 'l09-02-q3',
          question: { fr: 'Le résultat d\'exploitation est:', ru: 'Операционный результат — это:' },
          options: [
            { id: 'a', text: { fr: 'Produits financiers - Charges financières', ru: 'Финансовые доходы - Финансовые расходы' }, isCorrect: false },
            { id: 'b', text: { fr: "Produits d'exploitation - Charges d'exploitation", ru: 'Операционные доходы - Операционные расходы' }, isCorrect: true },
            { id: 'c', text: { fr: 'Résultat net - IS', ru: 'Чистая прибыль - IS' }, isCorrect: false },
            { id: 'd', text: { fr: 'Total produits - IS', ru: 'Итого доходы - IS' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL 10 LESSONS ────────────────────────────────────────────────────────

const l_10_01: LessonData = {
  id: 'l-10-01',
  title: { fr: 'Clôture des comptes', ru: 'Закрытие счетов' },
  steps: [
    {
      type: 'theory',
      title: { fr: "Fin d'exercice comptable", ru: 'Конец отчётного периода' },
      content: {
        fr: `La **clôture des comptes** est l'ensemble des opérations effectuées en fin d'exercice.

**Étapes de clôture:**
1. Inventaire physique des stocks
2. Calcul et enregistrement des amortissements
3. Comptabilisation des provisions
4. Régularisation des charges et produits
5. Détermination du résultat
6. Affectation du résultat (dividendes, réserves)
7. Établissement du bilan et du compte de résultat

**Délais légaux:**
- Clôture: 31/12 (ou autre date choisie)
- Dépôt des comptes: 6 mois après la clôture
- Approbation par l'assemblée générale: dans les 6 mois`,
        ru: `**Закрытие счетов** — совокупность операций, выполняемых в конце отчётного периода.

**Этапы закрытия:**
1. Физическая инвентаризация запасов
2. Расчёт и запись амортизации
3. Учёт резервов
4. Регулирование расходов и доходов
5. Определение результата
6. Распределение результата (дивиденды, резервы)
7. Составление баланса и отчёта о прибылях

**Законодательные сроки:**
- Закрытие: 31/12 (или другая выбранная дата)
- Подача счетов: 6 месяцев после закрытия
- Утверждение общим собранием: в течение 6 месяцев`,
      },
      keyConceptFr: 'La clôture annuelle est obligatoire. Elle permet de mesurer la performance de l\'année et de préparer les états financiers.',
      keyConceptRu: 'Годовое закрытие обязательно. Оно позволяет измерить эффективность года и подготовить финансовую отчётность.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Affectation du résultat', ru: 'Пример: Распределение результата' },
      content: {
        fr: `**Résultat net de l'exercice: 20 000 €** **Décision de l'assemblée générale:**
- Réserve légale (5%): 1 000 €
- Réserves facultatives: 5 000 €
- Dividendes aux actionnaires: 14 000 €

| Compte | Débit | Crédit |
|--------|-------|--------|
| Résultat de l'exercice (120) | 20 000 € | |
| Réserve légale (1061) | | 1 000 € |
| Réserves facultatives (1068) | | 5 000 € |
| Dividendes à payer (457) | | 14 000 € |`,
        ru: `**Чистая прибыль за год: 20 000 €**

**Решение общего собрания:**
- Обязательный резерв (5%): 1 000 €
- Добровольные резервы: 5 000 €
- Дивиденды акционерам: 14 000 €

| Счёт | Дебет | Кредит |
|------|-------|--------|
| Результат года (120) | 20 000 € | |
| Обязательный резерв (1061) | | 1 000 € |
| Добровольные резервы (1068) | | 5 000 € |
| Дивиденды к уплате (457) | | 14 000 € |`,
      },
      noteToRememberFr: 'La réserve légale est obligatoire jusqu\'à ce qu\'elle atteigne 10% du capital social.',
      noteToRememberRu: 'Обязательный резерв является обязательным до достижения 10% уставного капитала.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Clôture amortissement', ru: 'Упражнение: Закрытие амортизации' },
      question: {
        fr: 'Dotation aux amortissements annuelle 5 000 €:',
        ru: 'Ежегодные амортизационные отчисления 5 000 €:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Dotations aux amortissements (681)', ru: 'Амортизационные отчисления (681)' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Amortissements des immobilisations (28x)', ru: 'Амортизация основных средств (28x)' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Clôture des comptes', ru: 'Тест — Закрытие счетов' },
      questions: [
        {
          id: 'l10-01-q1',
          question: { fr: 'La clôture des comptes a lieu:', ru: 'Закрытие счетов происходит:' },
          options: [
            { id: 'a', text: { fr: 'Chaque mois', ru: 'Каждый месяц' }, isCorrect: false },
            { id: 'b', text: { fr: "En fin d'exercice", ru: 'В конце отчётного периода' }, isCorrect: true },
            { id: 'c', text: { fr: 'Chaque semaine', ru: 'Каждую неделю' }, isCorrect: false },
            { id: 'd', text: { fr: 'Tous les 5 ans', ru: 'Каждые 5 лет' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-01-q2',
          question: { fr: 'La réserve légale est obligatoire jusqu\'à:', ru: 'Обязательный резерв является обязательным до:' },
          options: [
            { id: 'a', text: { fr: '5% du capital', ru: '5% капитала' }, isCorrect: false },
            { id: 'b', text: { fr: '10% du capital', ru: '10% капитала' }, isCorrect: true },
            { id: 'c', text: { fr: '20% du capital', ru: '20% капитала' }, isCorrect: false },
            { id: 'd', text: { fr: '50% du capital', ru: '50% капитала' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-01-q3',
          question: { fr: 'Le délai de dépôt des comptes annuels est de:', ru: 'Срок подачи годовых счетов:' },
          options: [
            { id: 'a', text: { fr: '3 mois après la clôture', ru: '3 месяца после закрытия' }, isCorrect: false },
            { id: 'b', text: { fr: '6 mois après la clôture', ru: '6 месяцев после закрытия' }, isCorrect: true },
            { id: 'c', text: { fr: '1 an après la clôture', ru: '1 год после закрытия' }, isCorrect: false },
            { id: 'd', text: { fr: '1 mois après la clôture', ru: '1 месяц после закрытия' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_10_02: LessonData = {
  id: 'l-10-02',
  title: { fr: 'Audit et contrôle', ru: 'Аудит и контроль' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Vérification des comptes', ru: 'Проверка счетов' },
      content: {
        fr: `L'**audit comptable** est la vérification indépendante des comptes d'une entreprise.

**Types d'audit:**
- **Audit légal** (commissariat aux comptes): obligatoire pour les grandes entreprises
- **Audit contractuel**: à la demande de l'entreprise - **Audit interne**: réalisé par les employés de l'entreprise

**Objectifs:**
- Vérifier la régularité et la sincérité des comptes
- Détecter les fraudes et erreurs
- Certifier que les comptes donnent une image fidèle

**Rapport d'audit:**
- Certification sans réserve: comptes conformes
- Certification avec réserves: problèmes mineurs
- Refus de certifier: problèmes majeurs`,
        ru: `**Бухгалтерский аудит** — независимая проверка счетов предприятия.

**Виды аудита:**
- **Обязательный аудит** (ревизия): обязателен для крупных предприятий
- **Договорной аудит**: по запросу предприятия
- **Внутренний аудит**: проводится сотрудниками предприятия

**Цели:**
- Проверка правильности и достоверности счетов
- Выявление мошенничества и ошибок
- Подтверждение, что счета дают достоверное представление

**Аудиторское заключение:**
- Безоговорочное подтверждение: счета соответствуют
- Подтверждение с оговорками: незначительные проблемы
- Отказ от подтверждения: серьёзные проблемы`,
      },
      keyConceptFr: 'L\'audit garantit la fiabilité des informations financières. Il protège les investisseurs, les banques et l\'État.',
      keyConceptRu: 'Аудит гарантирует достоверность финансовой информации. Он защищает инвесторов, банки и государство.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Processus d\'audit', ru: 'Пример: Процесс аудита' },
      content: {
        fr: `**Étapes d'un audit légal:**

| Étape | Action | Durée |
|-------|--------|-------|
| 1 | Prise de connaissance de l'entreprise | 1 semaine |
| 2 | Évaluation des risques | 1 semaine |
| 3 | Tests des contrôles internes | 2 semaines |
| 4 | Contrôle des comptes | 3 semaines |
| 5 | Rédaction du rapport | 1 semaine |

**Résultat:** Le commissaire aux comptes certifie (ou non) les comptes annuels.`,
        ru: `**Этапы обязательного аудита:**

| Этап | Действие | Продолжительность |
|------|----------|-------------------|
| 1 | Знакомство с предприятием | 1 неделя |
| 2 | Оценка рисков | 1 неделя |
| 3 | Тестирование внутреннего контроля | 2 недели |
| 4 | Проверка счетов | 3 недели |
| 5 | Составление отчёта | 1 неделя |

**Результат:** Ревизор подтверждает (или нет) годовые счета.`,
      },
      noteToRememberFr: 'L\'audit légal est obligatoire pour les SA et les SARL dépassant certains seuils (CA > 3,1M€ ou bilan > 1,55M€).',
      noteToRememberRu: 'Обязательный аудит обязателен для АО и ООО, превышающих определённые пороги (оборот > 3,1 млн € или баланс > 1,55 млн €).',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice: Honoraires d\'audit', ru: 'Упражнение: Гонорары аудитора' },
      question: {
        fr: 'Paiement honoraires commissaire aux comptes 5 000 €:',
        ru: 'Оплата гонораров ревизора 5 000 €:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Honoraires (622) — charge', ru: 'Гонорары (622) — расход' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Banque (512) — paiement', ru: 'Банк (512) — оплата' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz — Audit et contrôle', ru: 'Тест — Аудит и контроль' },
      questions: [
        {
          id: 'l10-02-q1',
          question: { fr: 'L\'audit légal est réalisé par:', ru: 'Обязательный аудит проводится:' },
          options: [
            { id: 'a', text: { fr: 'Le comptable interne', ru: 'Внутренним бухгалтером' }, isCorrect: false },
            { id: 'b', text: { fr: 'Le commissaire aux comptes', ru: 'Ревизором' }, isCorrect: true },
            { id: 'c', text: { fr: 'Le directeur financier', ru: 'Финансовым директором' }, isCorrect: false },
            { id: 'd', text: { fr: "L'administration fiscale", ru: 'Налоговой администрацией' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-02-q2',
          question: { fr: 'Une certification sans réserve signifie:', ru: 'Безоговорочное подтверждение означает:' },
          options: [
            { id: 'a', text: { fr: 'Les comptes ont des problèmes majeurs', ru: 'Счета имеют серьёзные проблемы' }, isCorrect: false },
            { id: 'b', text: { fr: 'Les comptes sont conformes', ru: 'Счета соответствуют требованиям' }, isCorrect: true },
            { id: 'c', text: { fr: "L'audit est refusé", ru: 'Аудит отклонён' }, isCorrect: false },
            { id: 'd', text: { fr: 'Les comptes sont faux', ru: 'Счета ложные' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-02-q3',
          question: { fr: "L'objectif principal de l'audit est:", ru: 'Основная цель аудита:' },
          options: [
            { id: 'a', text: { fr: 'Réduire les impôts', ru: 'Снизить налоги' }, isCorrect: false },
            { id: 'b', text: { fr: 'Vérifier que les comptes donnent une image fidèle', ru: 'Проверить, что счета дают достоверное представление' }, isCorrect: true },
            { id: 'c', text: { fr: 'Augmenter les profits', ru: 'Увеличить прибыль' }, isCorrect: false },
            { id: 'd', text: { fr: 'Recruter des comptables', ru: 'Нанимать бухгалтеров' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

const l_10_03: LessonData = {
  id: 'l-10-03',
  title: { fr: 'Certification professionnelle', ru: 'Профессиональная сертификация' },
  steps: [
    {
      type: 'theory',
      title: { fr: 'Examen final — Tout intégrer', ru: 'Финальный экзамен — Интеграция всего' },
      content: {
        fr: `Félicitations! Vous avez atteint le **niveau expert** de Vika Comptable.

**Récapitulatif du parcours:**
- Niveau 1-2: Bases et concepts comptables
- Niveau 3: Débit et crédit
- Niveau 4: Écritures comptables
- Niveau 5: Factures et TVA
- Niveau 6: Déclarations fiscales
- Niveau 7: Grand livre et balance
- Niveau 8: Bilan
- Niveau 9: Compte de résultat
- Niveau 10: Clôture et audit

**Compétences acquises:**
✓ Enregistrer toutes les transactions
✓ Préparer les états financiers
✓ Calculer et déclarer la TVA
✓ Analyser un bilan
✓ Comprendre l'audit`,
        ru: `Поздравляем! Вы достигли **экспертного уровня** Vika Comptable.

**Резюме пути:**
- Уровень 1-2: Основы и концепции бухгалтерии
- Уровень 3: Дебет и кредит
- Уровень 4: Бухгалтерские записи
- Уровень 5: Счета-фактуры и НДС
- Уровень 6: Налоговые декларации
- Уровень 7: Главная книга и баланс
- Уровень 8: Баланс
- Уровень 9: Отчёт о прибылях
- Уровень 10: Закрытие и аудит

**Приобретённые навыки:**
✓ Записывать все операции
✓ Подготавливать финансовую отчётность
✓ Рассчитывать и декларировать НДС
✓ Анализировать баланс
✓ Понимать аудит`,
      },
      keyConceptFr: 'La comptabilité est un langage universel. Maîtriser ces concepts vous ouvre les portes de la finance et de la gestion d\'entreprise.',
      keyConceptRu: 'Бухгалтерия — универсальный язык. Освоение этих концепций открывает двери в финансы и управление предприятием.',
    },
    {
      type: 'example',
      title: { fr: 'Exemple: Cycle comptable complet', ru: 'Пример: Полный бухгалтерский цикл' },
      content: {
        fr: `**Le cycle comptable annuel:**

| Étape | Action | Période |
|-------|--------|---------|
| 1 | Enregistrement des transactions | Tout l'année | | 2 | Tenue du grand livre | Tout l'année |
| 3 | Balance de vérification | Mensuelle |
| 4 | Déclarations TVA | Mensuelle/Trimestrielle |
| 5 | Inventaire | 31/12 |
| 6 | Clôture des comptes | 31/12 |
| 7 | Bilan + Compte de résultat | Janvier |
| 8 | Audit | Janvier-Juin |
| 9 | Approbation AG | Avant 30/06 |`,
        ru: `**Годовой бухгалтерский цикл:**

| Этап | Действие | Период |
|------|----------|--------|
| 1 | Запись операций | Весь год |
| 2 | Ведение главной книги | Весь год |
| 3 | Проверочный баланс | Ежемесячно |
| 4 | Декларации НДС | Ежемесячно/Ежеквартально |
| 5 | Инвентаризация | 31/12 |
| 6 | Закрытие счетов | 31/12 |
| 7 | Баланс + Отчёт о прибылях | Январь |
| 8 | Аудит | Январь-Июнь |
| 9 | Утверждение ОС | До 30/06 |`,
      },
      noteToRememberFr: 'Vous êtes maintenant prêt(e) pour une carrière en comptabilité! Continuez à pratiquer avec des cas réels.',
      noteToRememberRu: 'Теперь вы готовы к карьере в бухгалтерии! Продолжайте практиковаться на реальных случаях.',
    },
    {
      type: 'exercise',
      title: { fr: 'Exercice final: Écriture complexe', ru: 'Финальное упражнение: Сложная запись' },
      question: {
        fr: 'Vente 10 000 € HT + TVA 2 000 € encaissée en banque. Enregistrez la vente:',
        ru: 'Продажа 10 000 € без НДС + НДС 2 000 €, зачислено в банк. Запишите продажу:',
      },
      items: [
        { id: 'ex-01', account: { fr: 'Banque (512) — 12 000 € TTC', ru: 'Банк (512) — 12 000 € с НДС' }, correctAnswer: 'debit' },
        { id: 'ex-02', account: { fr: 'Ventes (707) — 10 000 € HT', ru: 'Продажи (707) — 10 000 € без НДС' }, correctAnswer: 'credit' },
      ],
    },
    {
      type: 'quiz',
      title: { fr: 'Quiz final — Expert Comptable', ru: 'Финальный тест — Эксперт-бухгалтер' },
      questions: [
        {
          id: 'l10-03-q1',
          question: { fr: 'Le cycle comptable annuel se termine par:', ru: 'Годовой бухгалтерский цикл завершается:' },
          options: [
            { id: 'a', text: { fr: 'La balance de vérification', ru: 'Проверочным балансом' }, isCorrect: false },
            { id: 'b', text: { fr: "L'approbation des comptes par l'assemblée générale", ru: 'Утверждением счетов общим собранием' }, isCorrect: true },
            { id: 'c', text: { fr: 'La déclaration de TVA', ru: 'Декларацией НДС' }, isCorrect: false },
            { id: 'd', text: { fr: "L'inventaire", ru: 'Инвентаризацией' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-03-q2',
          question: { fr: 'L\'équation comptable fondamentale est:', ru: 'Фундаментальное бухгалтерское уравнение:' },
          options: [
            { id: 'a', text: { fr: 'Actif = Passif - Capitaux propres', ru: 'Актив = Пассив - Собственный капитал' }, isCorrect: false },
            { id: 'b', text: { fr: 'Actif = Passif + Capitaux propres', ru: 'Актив = Пассив + Собственный капитал' }, isCorrect: true },
            { id: 'c', text: { fr: 'Résultat = Produits + Charges', ru: 'Результат = Доходы + Расходы' }, isCorrect: false },
            { id: 'd', text: { fr: 'TVA = HT × 10%', ru: 'НДС = Без НДС × 10%' }, isCorrect: false },
          ],
        },
        {
          id: 'l10-03-q3',
          question: { fr: 'Un bénéfice net de 20 000 € avec IS 25% donne un résultat net de:', ru: 'Прибыль до налогов 20 000 € при IS 25% даёт чистую прибыль:' },
          options: [
            { id: 'a', text: { fr: '20 000 €', ru: '20 000 €' }, isCorrect: false },
            { id: 'b', text: { fr: '15 000 €', ru: '15 000 €' }, isCorrect: true },
            { id: 'c', text: { fr: '25 000 €', ru: '25 000 €' }, isCorrect: false },
            { id: 'd', text: { fr: '18 000 €', ru: '18 000 €' }, isCorrect: false },
          ],
        },
      ],
    },
  ],
};

// ─── LEVEL TESTS (unique per level) ──────────────────────────────────────────

export const levelTests: LevelTest[] = [
  {
    levelNumber: 1,
    title: { fr: 'Test de niveau 1 — Introduction', ru: 'Тест уровня 1 — Введение' },
    passingScore: 70,
    questions: [
      {
        id: 'lt1-q1',
        question: { fr: 'La comptabilité est souvent appelée:', ru: 'Бухгалтерию часто называют:' },
        options: [
          { id: 'a', text: { fr: 'Le langage des affaires', ru: 'Языком бизнеса' }, isCorrect: true },
          { id: 'b', text: { fr: 'Le langage des sciences', ru: 'Языком науки' }, isCorrect: false },
          { id: 'c', text: { fr: 'Le langage des arts', ru: 'Языком искусства' }, isCorrect: false },
          { id: 'd', text: { fr: 'Le langage du sport', ru: 'Языком спорта' }, isCorrect: false },
        ],
      },
      {
        id: 'lt1-q2',
        question: { fr: 'Qui certifie les comptes des grandes entreprises?', ru: 'Кто удостоверяет счета крупных предприятий?' },
        options: [
          { id: 'a', text: { fr: 'Le comptable interne', ru: 'Внутренний бухгалтер' }, isCorrect: false },
          { id: 'b', text: { fr: 'Le commissaire aux comptes', ru: 'Ревизор' }, isCorrect: true },
          { id: 'c', text: { fr: 'Le directeur commercial', ru: 'Коммерческий директор' }, isCorrect: false },
          { id: 'd', text: { fr: 'Le responsable RH', ru: 'Менеджер по персоналу' }, isCorrect: false },
        ],
      },
      {
        id: 'lt1-q3',
        question: { fr: 'Quel document prouve qu\'une vente a eu lieu?', ru: 'Какой документ подтверждает, что продажа состоялась?' },
        options: [
          { id: 'a', text: { fr: 'Le relevé bancaire', ru: 'Банковская выписка' }, isCorrect: false },
          { id: 'b', text: { fr: 'La facture', ru: 'Счёт-фактура' }, isCorrect: true },
          { id: 'c', text: { fr: 'Le bilan', ru: 'Баланс' }, isCorrect: false },
          { id: 'd', text: { fr: 'Le grand livre', ru: 'Главная книга' }, isCorrect: false },
        ],
      },
      {
        id: 'lt1-q4',
        question: { fr: 'Combien de temps faut-il conserver les documents comptables?', ru: 'Сколько лет нужно хранить бухгалтерские документы?' },
        options: [
          { id: 'a', text: { fr: '3 ans', ru: '3 года' }, isCorrect: false },
          { id: 'b', text: { fr: '5 ans', ru: '5 лет' }, isCorrect: false },
          { id: 'c', text: { fr: '10 ans', ru: '10 лет' }, isCorrect: true },
          { id: 'd', text: { fr: '1 an', ru: '1 год' }, isCorrect: false },
        ],
      },
      {
        id: 'lt1-q5',
        question: { fr: 'Le journal comptable est:', ru: 'Бухгалтерский журнал — это:' },
        options: [
          { id: 'a', text: { fr: 'Un récapitulatif annuel', ru: 'Годовая сводка' }, isCorrect: false },
          { id: 'b', text: { fr: 'Un registre chronologique des écritures', ru: 'Хронологический реестр записей' }, isCorrect: true },
          { id: 'c', text: { fr: 'Une liste des employés', ru: 'Список сотрудников' }, isCorrect: false },
          { id: 'd', text: { fr: 'Un document fiscal', ru: 'Налоговый документ' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 2,
    title: { fr: 'Test de niveau 2 — Concepts', ru: 'Тест уровня 2 — Концепции' },
    passingScore: 70,
    questions: [
      {
        id: 'lt2-q1',
        question: { fr: "L'équation comptable fondamentale est:", ru: 'Фундаментальное бухгалтерское уравнение:' },
        options: [
          { id: 'a', text: { fr: 'Actif = Passif - Capitaux propres', ru: 'Актив = Пассив - Собственный капитал' }, isCorrect: false },
          { id: 'b', text: { fr: 'Actif = Passif + Capitaux propres', ru: 'Актив = Пассив + Собственный капитал' }, isCorrect: true },
          { id: 'c', text: { fr: 'Passif = Actif + Capitaux propres', ru: 'Пассив = Актив + Собственный капитал' }, isCorrect: false },
          { id: 'd', text: { fr: 'Capitaux propres = Actif + Passif', ru: 'Собственный капитал = Актив + Пассив' }, isCorrect: false },
        ],
      },
      {
        id: 'lt2-q2',
        question: { fr: 'Le principe de prudence dit de:', ru: 'Принцип осторожности говорит:' },
        options: [
          { id: 'a', text: { fr: 'Anticiper les profits futurs', ru: 'Предвосхищать будущие прибыли' }, isCorrect: false },
          { id: 'b', text: { fr: 'Enregistrer les pertes probables immédiatement', ru: 'Немедленно записывать вероятные убытки' }, isCorrect: true },
          { id: 'c', text: { fr: 'Ignorer les risques', ru: 'Игнорировать риски' }, isCorrect: false },
          { id: 'd', text: { fr: 'Surestimer les actifs', ru: 'Завышать активы' }, isCorrect: false },
        ],
      },
      {
        id: 'lt2-q3',
        question: { fr: 'Le principe de continuité suppose que:', ru: 'Принцип непрерывности предполагает, что:' },
        options: [
          { id: 'a', text: { fr: "L'entreprise va fermer", ru: 'Предприятие закроется' }, isCorrect: false },
          { id: 'b', text: { fr: "L'entreprise continue son activité", ru: 'Предприятие продолжит деятельность' }, isCorrect: true },
          { id: 'c', text: { fr: "L'entreprise va être vendue", ru: 'Предприятие будет продано' }, isCorrect: false },
          { id: 'd', text: { fr: "L'entreprise est en faillite", ru: 'Предприятие банкрот' }, isCorrect: false },
        ],
      },
      {
        id: 'lt2-q4',
        question: { fr: 'Si Actif = 100 000 € et Passif = 60 000 €, les Capitaux propres sont:', ru: 'Если Актив = 100 000 € и Пассив = 60 000 €, то Собственный капитал:' },
        options: [
          { id: 'a', text: { fr: '160 000 €', ru: '160 000 €' }, isCorrect: false },
          { id: 'b', text: { fr: '40 000 €', ru: '40 000 €' }, isCorrect: true },
          { id: 'c', text: { fr: '60 000 €', ru: '60 000 €' }, isCorrect: false },
          { id: 'd', text: { fr: '100 000 €', ru: '100 000 €' }, isCorrect: false },
        ],
      },
      {
        id: 'lt2-q5',
        question: { fr: "Une provision est créée pour:", ru: 'Резерв создаётся для:' },
        options: [
          { id: 'a', text: { fr: 'Augmenter les profits', ru: 'Увеличения прибыли' }, isCorrect: false },
          { id: 'b', text: { fr: 'Couvrir un risque futur probable', ru: 'Покрытия вероятного будущего риска' }, isCorrect: true },
          { id: 'c', text: { fr: 'Réduire les impôts', ru: 'Снижения налогов' }, isCorrect: false },
          { id: 'd', text: { fr: 'Payer les salaires', ru: 'Выплаты зарплат' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 3,
    title: { fr: 'Test de niveau 3 — Débit/Crédit', ru: 'Тест уровня 3 — Дебет/Кредит' },
    passingScore: 70,
    questions: [
      {
        id: 'lt3-q1',
        question: { fr: 'Le débit augmente:', ru: 'Дебет увеличивает:' },
        options: [
          { id: 'a', text: { fr: 'Les passifs et les produits', ru: 'Пассивы и доходы' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les actifs et les charges', ru: 'Активы и расходы' }, isCorrect: true },
          { id: 'c', text: { fr: 'Seulement les actifs', ru: 'Только активы' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les capitaux propres', ru: 'Собственный капитал' }, isCorrect: false },
        ],
      },
      {
        id: 'lt3-q2',
        question: { fr: 'Dans un compte en T, le débit est:', ru: 'В Т-счёте дебет находится:' },
        options: [
          { id: 'a', text: { fr: 'À droite', ru: 'Справа' }, isCorrect: false },
          { id: 'b', text: { fr: 'À gauche', ru: 'Слева' }, isCorrect: true },
          { id: 'c', text: { fr: 'Au centre', ru: 'В центре' }, isCorrect: false },
          { id: 'd', text: { fr: 'En bas', ru: 'Внизу' }, isCorrect: false },
        ],
      },
      {
        id: 'lt3-q3',
        question: { fr: 'Le crédit augmente:', ru: 'Кредит увеличивает:' },
        options: [
          { id: 'a', text: { fr: 'Les actifs et les charges', ru: 'Активы и расходы' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les passifs et les produits', ru: 'Пассивы и доходы' }, isCorrect: true },
          { id: 'c', text: { fr: 'Seulement les passifs', ru: 'Только пассивы' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les charges uniquement', ru: 'Только расходы' }, isCorrect: false },
        ],
      },
      {
        id: 'lt3-q4',
        question: { fr: "Dans une écriture, le total des débits doit:", ru: 'В записи сумма дебетов должна:' },
        options: [
          { id: 'a', text: { fr: 'Être supérieur aux crédits', ru: 'Превышать кредиты' }, isCorrect: false },
          { id: 'b', text: { fr: 'Être égal aux crédits', ru: 'Быть равна кредитам' }, isCorrect: true },
          { id: 'c', text: { fr: 'Être inférieur aux crédits', ru: 'Быть меньше кредитов' }, isCorrect: false },
          { id: 'd', text: { fr: "N'avoir aucun lien", ru: 'Не иметь связи' }, isCorrect: false },
        ],
      },
      {
        id: 'lt3-q5',
        question: { fr: "L'entreprise achète du matériel 1 000 € en espèces. Le compte Caisse est:", ru: 'Предприятие покупает оборудование за 1 000 € наличными. Счёт Касса:' },
        options: [
          { id: 'a', text: { fr: 'Débité', ru: 'Дебетуется' }, isCorrect: false },
          { id: 'b', text: { fr: 'Crédité', ru: 'Кредитуется' }, isCorrect: true },
          { id: 'c', text: { fr: 'Inchangé', ru: 'Не изменяется' }, isCorrect: false },
          { id: 'd', text: { fr: 'Annulé', ru: 'Аннулируется' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 4,
    title: { fr: 'Test de niveau 4 — Écritures', ru: 'Тест уровня 4 — Записи' },
    passingScore: 70,
    questions: [
      {
        id: 'lt4-q1',
        question: { fr: 'Les comptes de classe 6 sont:', ru: 'Счета класса 6 — это:' },
        options: [
          { id: 'a', text: { fr: 'Des comptes de produits', ru: 'Счета доходов' }, isCorrect: false },
          { id: 'b', text: { fr: 'Des comptes de charges', ru: 'Счета расходов' }, isCorrect: true },
          { id: 'c', text: { fr: 'Des comptes de tiers', ru: 'Счета третьих лиц' }, isCorrect: false },
          { id: 'd', text: { fr: 'Des comptes financiers', ru: 'Финансовые счета' }, isCorrect: false },
        ],
      },
      {
        id: 'lt4-q2',
        question: { fr: 'Lors d\'une vente à crédit, on débite:', ru: 'При продаже в кредит дебетуется:' },
        options: [
          { id: 'a', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les créances clients', ru: 'Дебиторская задолженность' }, isCorrect: true },
          { id: 'c', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les charges', ru: 'Расходы' }, isCorrect: false },
        ],
      },
      {
        id: 'lt4-q3',
        question: { fr: 'Le compte 401 représente:', ru: 'Счёт 401 представляет:' },
        options: [
          { id: 'a', text: { fr: 'Les clients', ru: 'Клиентов' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les fournisseurs', ru: 'Поставщиков' }, isCorrect: true },
          { id: 'c', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
        ],
      },
      {
        id: 'lt4-q4',
        question: { fr: 'Lors d\'un achat à crédit, on crédite:', ru: 'При покупке в кредит кредитуется:' },
        options: [
          { id: 'a', text: { fr: 'Les achats', ru: 'Покупки' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les dettes fournisseurs', ru: 'Долги поставщикам' }, isCorrect: true },
          { id: 'c', text: { fr: 'La banque', ru: 'Банк' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les ventes', ru: 'Продажи' }, isCorrect: false },
        ],
      },
      {
        id: 'lt4-q5',
        question: { fr: 'Le compte 512 correspond à:', ru: 'Счёт 512 соответствует:' },
        options: [
          { id: 'a', text: { fr: 'Les achats', ru: 'Покупкам' }, isCorrect: false },
          { id: 'b', text: { fr: 'La banque', ru: 'Банку' }, isCorrect: true },
          { id: 'c', text: { fr: 'Les ventes', ru: 'Продажам' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les stocks', ru: 'Запасам' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 5,
    title: { fr: 'Test de niveau 5 — Factures', ru: 'Тест уровня 5 — Счета-фактуры' },
    passingScore: 70,
    questions: [
      {
        id: 'lt5-q1',
        question: { fr: 'TTC signifie:', ru: 'TTC означает:' },
        options: [
          { id: 'a', text: { fr: 'Toutes Taxes Comprises', ru: 'Все налоги включены' }, isCorrect: true },
          { id: 'b', text: { fr: 'Taxe Totale Calculée', ru: 'Общий рассчитанный налог' }, isCorrect: false },
          { id: 'c', text: { fr: 'Taux de TVA Calculé', ru: 'Рассчитанная ставка НДС' }, isCorrect: false },
          { id: 'd', text: { fr: 'Total Taxe Comptable', ru: 'Общий бухгалтерский налог' }, isCorrect: false },
        ],
      },
      {
        id: 'lt5-q2',
        question: { fr: 'HT = 1 000 €, TVA 20%. Quel est le montant TTC?', ru: 'Без НДС = 1 000 €, НДС 20%. Какова сумма с НДС?' },
        options: [
          { id: 'a', text: { fr: '1 100 €', ru: '1 100 €' }, isCorrect: false },
          { id: 'b', text: { fr: '1 200 €', ru: '1 200 €' }, isCorrect: true },
          { id: 'c', text: { fr: '1 020 €', ru: '1 020 €' }, isCorrect: false },
          { id: 'd', text: { fr: '1 500 €', ru: '1 500 €' }, isCorrect: false },
        ],
      },
      {
        id: 'lt5-q3',
        question: { fr: 'La TVA collectée est:', ru: 'Собранный НДС — это:' },
        options: [
          { id: 'a', text: { fr: 'Un actif de l\'entreprise', ru: 'Актив предприятия' }, isCorrect: false },
          { id: 'b', text: { fr: 'Une dette envers l\'État', ru: 'Долг перед государством' }, isCorrect: true },
          { id: 'c', text: { fr: 'Un produit de l\'entreprise', ru: 'Доход предприятия' }, isCorrect: false },
          { id: 'd', text: { fr: 'Une charge de l\'entreprise', ru: 'Расход предприятия' }, isCorrect: false },
        ],
      },
      {
        id: 'lt5-q4',
        question: { fr: 'La TVA déductible est enregistrée:', ru: 'Вычитаемый НДС записывается:' },
        options: [
          { id: 'a', text: { fr: 'Au crédit', ru: 'По кредиту' }, isCorrect: false },
          { id: 'b', text: { fr: 'Au débit', ru: 'По дебету' }, isCorrect: true },
          { id: 'c', text: { fr: "Elle n'est pas enregistrée", ru: 'Он не записывается' }, isCorrect: false },
          { id: 'd', text: { fr: 'Dans les charges', ru: 'В расходах' }, isCorrect: false },
        ],
      },
      {
        id: 'lt5-q5',
        question: { fr: 'Le compte fournisseur (401) est crédité pour:', ru: 'Счёт поставщика (401) кредитуется на:' },
        options: [
          { id: 'a', text: { fr: 'Le montant HT', ru: 'Сумму без НДС' }, isCorrect: false },
          { id: 'b', text: { fr: 'Le montant TTC', ru: 'Сумму с НДС' }, isCorrect: true },
          { id: 'c', text: { fr: 'Le montant de TVA', ru: 'Сумму НДС' }, isCorrect: false },
          { id: 'd', text: { fr: 'La moitié du montant', ru: 'Половину суммы' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 6,
    title: { fr: 'Test de niveau 6 — TVA', ru: 'Тест уровня 6 — НДС' },
    passingScore: 70,
    questions: [
      {
        id: 'lt6-q1',
        question: { fr: 'Quel est le taux normal de TVA en France?', ru: 'Какова стандартная ставка НДС во Франции?' },
        options: [
          { id: 'a', text: { fr: '10%', ru: '10%' }, isCorrect: false },
          { id: 'b', text: { fr: '20%', ru: '20%' }, isCorrect: true },
          { id: 'c', text: { fr: '5,5%', ru: '5,5%' }, isCorrect: false },
          { id: 'd', text: { fr: '15%', ru: '15%' }, isCorrect: false },
        ],
      },
      {
        id: 'lt6-q2',
        question: { fr: 'TVA collectée 5 000 €, TVA déductible 3 000 €. TVA à payer:', ru: 'Собранный НДС 5 000 €, вычитаемый НДС 3 000 €. НДС к уплате:' },
        options: [
          { id: 'a', text: { fr: '8 000 €', ru: '8 000 €' }, isCorrect: false },
          { id: 'b', text: { fr: '2 000 €', ru: '2 000 €' }, isCorrect: true },
          { id: 'c', text: { fr: '3 000 €', ru: '3 000 €' }, isCorrect: false },
          { id: 'd', text: { fr: '5 000 €', ru: '5 000 €' }, isCorrect: false },
        ],
      },
      {
        id: 'lt6-q3',
        question: { fr: 'La CA3 doit être déposée avant le:', ru: 'CA3 должна быть подана до:' },
        options: [
          { id: 'a', text: { fr: '1er du mois suivant', ru: '1-го числа следующего месяца' }, isCorrect: false },
          { id: 'b', text: { fr: '19 du mois suivant', ru: '19-го числа следующего месяца' }, isCorrect: true },
          { id: 'c', text: { fr: '31 du même mois', ru: '31-го числа того же месяца' }, isCorrect: false },
          { id: 'd', text: { fr: '15 du mois suivant', ru: '15-го числа следующего месяца' }, isCorrect: false },
        ],
      },
      {
        id: 'lt6-q4',
        question: { fr: 'Qui supporte réellement la TVA?', ru: 'Кто реально несёт НДС?' },
        options: [
          { id: 'a', text: { fr: "L'entreprise", ru: 'Предприятие' }, isCorrect: false },
          { id: 'b', text: { fr: 'Le consommateur final', ru: 'Конечный потребитель' }, isCorrect: true },
          { id: 'c', text: { fr: "L'État", ru: 'Государство' }, isCorrect: false },
          { id: 'd', text: { fr: 'Le fournisseur', ru: 'Поставщик' }, isCorrect: false },
        ],
      },
      {
        id: 'lt6-q5',
        question: { fr: 'Un crédit de TVA signifie:', ru: 'Кредит НДС означает:' },
        options: [
          { id: 'a', text: { fr: "L'entreprise doit payer plus de TVA", ru: 'Предприятие должно заплатить больше НДС' }, isCorrect: false },
          { id: 'b', text: { fr: "L'État doit rembourser l'entreprise", ru: 'Государство должно возместить предприятию' }, isCorrect: true },
          { id: 'c', text: { fr: "La TVA est annulée", ru: 'НДС аннулируется' }, isCorrect: false },
          { id: 'd', text: { fr: "Il n'y a pas de TVA", ru: 'НДС нет' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 7,
    title: { fr: 'Test de niveau 7 — Grand Livre', ru: 'Тест уровня 7 — Главная книга' },
    passingScore: 70,
    questions: [
      {
        id: 'lt7-q1',
        question: { fr: 'Le grand livre regroupe:', ru: 'Главная книга объединяет:' },
        options: [
          { id: 'a', text: { fr: 'Seulement les comptes de banque', ru: 'Только банковские счета' }, isCorrect: false },
          { id: 'b', text: { fr: 'Tous les comptes de l\'entreprise', ru: 'Все счета предприятия' }, isCorrect: true },
          { id: 'c', text: { fr: 'Seulement les charges', ru: 'Только расходы' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les factures uniquement', ru: 'Только счета-фактуры' }, isCorrect: false },
        ],
      },
      {
        id: 'lt7-q2',
        question: { fr: 'Dans une balance équilibrée:', ru: 'В сбалансированном проверочном балансе:' },
        options: [
          { id: 'a', text: { fr: 'Débits > Crédits', ru: 'Дебеты > Кредиты' }, isCorrect: false },
          { id: 'b', text: { fr: 'Débits = Crédits', ru: 'Дебеты = Кредиты' }, isCorrect: true },
          { id: 'c', text: { fr: 'Crédits > Débits', ru: 'Кредиты > Дебеты' }, isCorrect: false },
          { id: 'd', text: { fr: 'Débits = 0', ru: 'Дебеты = 0' }, isCorrect: false },
        ],
      },
      {
        id: 'lt7-q3',
        question: { fr: 'Le solde d\'un compte actif est normalement:', ru: 'Остаток счёта актива обычно:' },
        options: [
          { id: 'a', text: { fr: 'Créditeur', ru: 'Кредитовый' }, isCorrect: false },
          { id: 'b', text: { fr: 'Débiteur', ru: 'Дебетовый' }, isCorrect: true },
          { id: 'c', text: { fr: 'Nul', ru: 'Нулевой' }, isCorrect: false },
          { id: 'd', text: { fr: 'Négatif', ru: 'Отрицательный' }, isCorrect: false },
        ],
      },
      {
        id: 'lt7-q4',
        question: { fr: 'Si la balance ne s\'équilibre pas:', ru: 'Если баланс не сходится:' },
        options: [
          { id: 'a', text: { fr: "L'entreprise est en faillite", ru: 'Предприятие банкрот' }, isCorrect: false },
          { id: 'b', text: { fr: "Il y a une erreur d'enregistrement", ru: 'Есть ошибка записи' }, isCorrect: true },
          { id: 'c', text: { fr: "Les ventes sont nulles", ru: 'Продажи нулевые' }, isCorrect: false },
          { id: 'd', text: { fr: "La TVA n'est pas payée", ru: 'НДС не уплачен' }, isCorrect: false },
        ],
      },
      {
        id: 'lt7-q5',
        question: { fr: 'La balance de vérification est établie:', ru: 'Проверочный баланс составляется:' },
        options: [
          { id: 'a', text: { fr: 'Après le bilan', ru: 'После баланса' }, isCorrect: false },
          { id: 'b', text: { fr: 'Avant le bilan', ru: 'До баланса' }, isCorrect: true },
          { id: 'c', text: { fr: 'En même temps que le bilan', ru: 'Одновременно с балансом' }, isCorrect: false },
          { id: 'd', text: { fr: "Elle n'est pas liée au bilan", ru: 'Она не связана с балансом' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 8,
    title: { fr: 'Test de niveau 8 — Bilan', ru: 'Тест уровня 8 — Баланс' },
    passingScore: 70,
    questions: [
      {
        id: 'lt8-q1',
        question: { fr: 'Le bilan présente la situation de l\'entreprise:', ru: 'Баланс представляет положение предприятия:' },
        options: [
          { id: 'a', text: { fr: 'Sur une période', ru: 'За период' }, isCorrect: false },
          { id: 'b', text: { fr: 'À une date donnée', ru: 'На определённую дату' }, isCorrect: true },
          { id: 'c', text: { fr: 'Sur 5 ans', ru: 'За 5 лет' }, isCorrect: false },
          { id: 'd', text: { fr: 'Chaque semaine', ru: 'Каждую неделю' }, isCorrect: false },
        ],
      },
      {
        id: 'lt8-q2',
        question: { fr: 'Les capitaux propres font partie:', ru: 'Собственный капитал является частью:' },
        options: [
          { id: 'a', text: { fr: "De l'actif", ru: 'Актива' }, isCorrect: false },
          { id: 'b', text: { fr: 'Du passif', ru: 'Пассива' }, isCorrect: true },
          { id: 'c', text: { fr: 'Des charges', ru: 'Расходов' }, isCorrect: false },
          { id: 'd', text: { fr: 'Des produits', ru: 'Доходов' }, isCorrect: false },
        ],
      },
      {
        id: 'lt8-q3',
        question: { fr: 'Un fonds de roulement positif signifie:', ru: 'Положительный рабочий капитал означает:' },
        options: [
          { id: 'a', text: { fr: "L'entreprise est en difficulté", ru: 'Предприятие в затруднении' }, isCorrect: false },
          { id: 'b', text: { fr: "L'entreprise finance correctement ses actifs LT", ru: 'Предприятие правильно финансирует долгосрочные активы' }, isCorrect: true },
          { id: 'c', text: { fr: "L'entreprise a trop de dettes", ru: 'Предприятие имеет слишком много долгов' }, isCorrect: false },
          { id: 'd', text: { fr: "L'entreprise n'a pas de clients", ru: 'У предприятия нет клиентов' }, isCorrect: false },
        ],
      },
      {
        id: 'lt8-q4',
        question: { fr: 'L\'actif circulant comprend:', ru: 'Оборотные активы включают:' },
        options: [
          { id: 'a', text: { fr: 'Les machines et bâtiments', ru: 'Машины и здания' }, isCorrect: false },
          { id: 'b', text: { fr: 'Les stocks, créances et trésorerie', ru: 'Запасы, дебиторскую задолженность и денежные средства' }, isCorrect: true },
          { id: 'c', text: { fr: 'Les emprunts bancaires', ru: 'Банковские кредиты' }, isCorrect: false },
          { id: 'd', text: { fr: 'Le capital social', ru: 'Уставный капитал' }, isCorrect: false },
        ],
      },
      {
        id: 'lt8-q5',
        question: { fr: 'La trésorerie nette = FR:', ru: 'Чистые денежные средства = FR:' },
        options: [
          { id: 'a', text: { fr: '+ BFR', ru: '+ BFR' }, isCorrect: false },
          { id: 'b', text: { fr: '- BFR', ru: '- BFR' }, isCorrect: true },
          { id: 'c', text: { fr: '× BFR', ru: '× BFR' }, isCorrect: false },
          { id: 'd', text: { fr: '÷ BFR', ru: '÷ BFR' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 9,
    title: { fr: 'Test de niveau 9 — Compte de résultat', ru: 'Тест уровня 9 — Отчёт о прибылях' },
    passingScore: 70,
    questions: [
      {
        id: 'lt9-q1',
        question: { fr: 'Le compte de résultat mesure:', ru: 'Отчёт о прибылях измеряет:' },
        options: [
          { id: 'a', text: { fr: 'La situation à une date donnée', ru: 'Положение на определённую дату' }, isCorrect: false },
          { id: 'b', text: { fr: 'La performance sur une période', ru: 'Эффективность за период' }, isCorrect: true },
          { id: 'c', text: { fr: 'Les dettes uniquement', ru: 'Только долги' }, isCorrect: false },
          { id: 'd', text: { fr: 'Les actifs uniquement', ru: 'Только активы' }, isCorrect: false },
        ],
      },
      {
        id: 'lt9-q2',
        question: { fr: 'Si Produits > Charges, l\'entreprise réalise:', ru: 'Если Доходы > Расходы, предприятие получает:' },
        options: [
          { id: 'a', text: { fr: 'Une perte', ru: 'Убыток' }, isCorrect: false },
          { id: 'b', text: { fr: 'Un bénéfice', ru: 'Прибыль' }, isCorrect: true },
          { id: 'c', text: { fr: 'Un résultat nul', ru: 'Нулевой результат' }, isCorrect: false },
          { id: 'd', text: { fr: 'Une dette', ru: 'Долг' }, isCorrect: false },
        ],
      },
      {
        id: 'lt9-q3',
        question: { fr: 'Le taux d\'IS en France est de:', ru: 'Ставка налога на прибыль во Франции:' },
        options: [
          { id: 'a', text: { fr: '20%', ru: '20%' }, isCorrect: false },
          { id: 'b', text: { fr: '25%', ru: '25%' }, isCorrect: true },
          { id: 'c', text: { fr: '33%', ru: '33%' }, isCorrect: false },
          { id: 'd', text: { fr: '15%', ru: '15%' }, isCorrect: false },
        ],
      },
      {
        id: 'lt9-q4',
        question: { fr: 'Les amortissements sont:', ru: 'Амортизация — это:' },
        options: [
          { id: 'a', text: { fr: 'Des produits', ru: 'Доходы' }, isCorrect: false },
          { id: 'b', text: { fr: 'Des charges', ru: 'Расходы' }, isCorrect: true },
          { id: 'c', text: { fr: 'Des actifs', ru: 'Активы' }, isCorrect: false },
          { id: 'd', text: { fr: 'Des dettes', ru: 'Долги' }, isCorrect: false },
        ],
      },
      {
        id: 'lt9-q5',
        question: { fr: 'Résultat net = Résultat avant IS:', ru: 'Чистая прибыль = Результат до налогов:' },
        options: [
          { id: 'a', text: { fr: '+ IS', ru: '+ IS' }, isCorrect: false },
          { id: 'b', text: { fr: '- IS', ru: '- IS' }, isCorrect: true },
          { id: 'c', text: { fr: '× IS', ru: '× IS' }, isCorrect: false },
          { id: 'd', text: { fr: '÷ IS', ru: '÷ IS' }, isCorrect: false },
        ],
      },
    ],
  },
  {
    levelNumber: 10,
    title: { fr: 'Test de niveau 10 — Expert Comptable', ru: 'Тест уровня 10 — Эксперт-бухгалтер' },
    passingScore: 70,
    questions: [
      {
        id: 'lt10-q1',
        question: { fr: 'La clôture des comptes a lieu:', ru: 'Закрытие счетов происходит:' },
        options: [
          { id: 'a', text: { fr: 'Chaque mois', ru: 'Каждый месяц' }, isCorrect: false },
          { id: 'b', text: { fr: "En fin d'exercice", ru: 'В конце отчётного периода' }, isCorrect: true },
          { id: 'c', text: { fr: 'Chaque semaine', ru: 'Каждую неделю' }, isCorrect: false },
          { id: 'd', text: { fr: 'Tous les 5 ans', ru: 'Каждые 5 лет' }, isCorrect: false },
        ],
      },
      {
        id: 'lt10-q2',
        question: { fr: 'L\'audit légal est réalisé par:', ru: 'Обязательный аудит проводится:' },
        options: [
          { id: 'a', text: { fr: 'Le comptable interne', ru: 'Внутренним бухгалтером' }, isCorrect: false },
          { id: 'b', text: { fr: 'Le commissaire aux comptes', ru: 'Ревизором' }, isCorrect: true },
          { id: 'c', text: { fr: 'Le directeur financier', ru: 'Финансовым директором' }, isCorrect: false },
          { id: 'd', text: { fr: "L'administration fiscale", ru: 'Налоговой администрацией' }, isCorrect: false },
        ],
      },
      {
        id: 'lt10-q3',
        question: { fr: 'La réserve légale est obligatoire jusqu\'à:', ru: 'Обязательный резерв является обязательным до:' },
        options: [
          { id: 'a', text: { fr: '5% du capital', ru: '5% капитала' }, isCorrect: false },
          { id: 'b', text: { fr: '10% du capital', ru: '10% капитала' }, isCorrect: true },
          { id: 'c', text: { fr: '20% du capital', ru: '20% капитала' }, isCorrect: false },
          { id: 'd', text: { fr: '50% du capital', ru: '50% капитала' }, isCorrect: false },
        ],
      },
      {
        id: 'lt10-q4',
        question: { fr: 'L\'équation comptable fondamentale est:', ru: 'Фундаментальное бухгалтерское уравнение:' },
        options: [
          { id: 'a', text: { fr: 'Actif = Passif - Capitaux propres', ru: 'Актив = Пассив - Собственный капитал' }, isCorrect: false },
          { id: 'b', text: { fr: 'Actif = Passif + Capitaux propres', ru: 'Актив = Пассив + Собственный капитал' }, isCorrect: true },
          { id: 'c', text: { fr: 'Résultat = Produits + Charges', ru: 'Результат = Доходы + Расходы' }, isCorrect: false },
          { id: 'd', text: { fr: 'TVA = HT × 10%', ru: 'НДС = Без НДС × 10%' }, isCorrect: false },
        ],
      },
      {
        id: 'lt10-q5',
        question: { fr: 'Un bénéfice avant IS de 20 000 € avec IS 25% donne un résultat net de:', ru: 'Прибыль до налогов 20 000 € при IS 25% даёт чистую прибыль:' },
        options: [
          { id: 'a', text: { fr: '20 000 €', ru: '20 000 €' }, isCorrect: false },
          { id: 'b', text: { fr: '15 000 €', ru: '15 000 €' }, isCorrect: true },
          { id: 'c', text: { fr: '25 000 €', ru: '25 000 €' }, isCorrect: false },
          { id: 'd', text: { fr: '18 000 €', ru: '18 000 €' }, isCorrect: false },
        ],
      },
    ],
  },
];

// ─── LESSON CONTENT MAP ───────────────────────────────────────────────────────

export const allLessonData: Record<string, LessonData> = {
  'l-01-01': l_01_01,
  'l-01-02': l_01_02,
  'l-01-03': l_01_03,
  'l-02-01': l_02_01,
  'l-02-02': l_02_02,
  'l-02-03': l_02_03,
  'l-03-01': l_03_01,
  'l-03-02': l_03_02,
  'l-03-03': l_03_03,
  'l-04-01': l_04_01,
  'l-04-02': l_04_02,
  'l-04-03': l_04_03,
  'l-05-01': l_05_01,
  'l-05-02': l_05_02,
  'l-05-03': l_05_03,
  'l-06-01': l_06_01,
  'l-06-02': l_06_02,
  'l-06-03': l_06_03,
  'l-07-01': l_07_01,
  'l-07-02': l_07_02,
  'l-08-01': l_08_01,
  'l-08-02': l_08_02,
  'l-09-01': l_09_01,
  'l-09-02': l_09_02,
  'l-10-01': l_10_01,
  'l-10-02': l_10_02,
  'l-10-03': l_10_03,
};
